import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import { FaBars, FaUpload, FaChevronDown, FaEye, FaEyeSlash, FaChevronLeft, FaChevronRight, FaCalendarAlt, FaPlus, FaTimes, FaChartBar, FaUser, FaCog, FaComments, FaBook, FaGraduationCap, FaSearch, FaCheck, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaEnvelope, FaExclamationTriangle, FaMapMarkerAlt, FaVideo, FaSchool, FaFileAlt, FaLightbulb, FaStar } from 'react-icons/fa';
import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { getTeacherBookingRequests, getTeacherRatingSummary } from './bookingService';
import { getPayoutHistory, getEligiblePayoutBookings } from './paymentService';
import RatingBreakdown from './components/RatingBreakdown';
import RecentReviews from './components/RecentReviews';
import LocationPicker from './components/location/LocationPicker';
import LocationList from './components/location/LocationList';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from './utils/cropImage';
import Step10Review from './components/wizard/Step10Review';
import { wizardTranslations } from './TeacherSignupWizard';

// Custom Time Picker Component
const CustomTimePicker = ({ value, onChange, isArabic, min }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Generate time slots (every 15 minutes from 00:00 to 23:45)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const hour12 = hour % 12 || 12;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const time12 = `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
        slots.push({ time24, time12, hour, minute });
      }
    }
    return slots;
  };
  
  const allTimeSlots = generateTimeSlots();
  
  // Quick time buttons (common times)
  const quickTimes = [
    { label: isArabic ? '8 صباحاً' : '8:00 AM', value: '08:00' },
    { label: isArabic ? '9 صباحاً' : '9:00 AM', value: '09:00' },
    { label: isArabic ? '10 صباحاً' : '10:00 AM', value: '10:00' },
    { label: isArabic ? '12 ظهراً' : '12:00 PM', value: '12:00' },
    { label: isArabic ? '2 مساءً' : '2:00 PM', value: '14:00' },
    { label: isArabic ? '4 مساءً' : '4:00 PM', value: '16:00' },
    { label: isArabic ? '6 مساءً' : '6:00 PM', value: '18:00' },
    { label: isArabic ? '8 مساءً' : '8:00 PM', value: '20:00' }
  ];
  
  // Filter time slots based on search and min time
  const filteredSlots = allTimeSlots.filter(slot => {
    const matchesSearch = !searchQuery || 
      slot.time12.toLowerCase().includes(searchQuery.toLowerCase()) ||
      slot.time24.includes(searchQuery);
    const meetsMin = !min || slot.time24 >= min;
    return matchesSearch && meetsMin;
  });
  
  // Format display value
  const formatDisplayTime = (time24) => {
    if (!time24) return isArabic ? 'اختر الوقت' : 'Select Time';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? (isArabic ? 'م' : 'PM') : (isArabic ? 'ص' : 'AM');
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  const handleSelect = (time24) => {
    onChange(time24);
    setIsOpen(false);
    setSearchQuery('');
  };
  
  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: isMobile ? '10px 14px' : '12px 16px',
          border: '2px solid #e5e7eb',
          borderRadius: isMobile ? 8 : 10,
          fontSize: isMobile ? 14 : 15,
          fontWeight: 600,
          outline: 'none',
          transition: 'all 0.2s',
          background: '#fff',
          color: '#111827',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left'
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#ff7a1a';
          e.currentTarget.style.boxShadow = '0 0 0 4px rgba(255, 122, 26, 0.1)';
        }}
        onBlur={(e) => {
          if (!isOpen) {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <span>{formatDisplayTime(value)}</span>
        <span style={{ 
          fontSize: 12, 
          color: '#6b7280',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>▼</span>
      </button>
      
      {isOpen && (
        <div style={{
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? '50%' : 'calc(100% + 8px)',
          left: isMobile ? '50%' : 0,
          right: isMobile ? 'auto' : 0,
          transform: isMobile ? 'translate(-50%, -50%)' : 'none',
          width: isMobile ? '90vw' : '100%',
          maxWidth: isMobile ? 400 : 'none',
          background: '#fff',
          borderRadius: isMobile ? 16 : 12,
          border: '2px solid #ff7a1a',
          boxShadow: isMobile 
            ? '0 20px 60px rgba(0,0,0,0.3), 0 8px 24px rgba(255, 122, 26, 0.3)' 
            : '0 8px 24px rgba(0,0,0,0.15), 0 4px 12px rgba(255, 122, 26, 0.2)',
          zIndex: 1000,
          maxHeight: isMobile ? '80vh' : 400,
          overflow: 'hidden',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          {/* Search Input */}
          <div style={{ padding: isMobile ? 10 : 12, borderBottom: '1px solid #e5e7eb' }}>
            <input
              type="text"
              placeholder={isArabic ? 'ابحث عن الوقت...' : 'Search time...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: isMobile ? 6 : 8,
                fontSize: isMobile ? 13 : 14,
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#ff7a1a';
                e.target.style.boxShadow = '0 0 0 3px rgba(255, 122, 26, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
              autoFocus
            />
          </div>
          
          {/* Quick Time Buttons */}
          {!searchQuery && (
            <div style={{ 
              padding: isMobile ? 10 : 12, 
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <div style={{ 
                fontSize: isMobile ? 11 : 12, 
                fontWeight: 700, 
                color: '#6b7280', 
                marginBottom: isMobile ? 6 : 8,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {isArabic ? 'أوقات سريعة' : 'Quick Times'}
              </div>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: isMobile ? 4 : 6 
              }}>
                {quickTimes.map((quickTime) => (
                  <button
                    key={quickTime.value}
                    type="button"
                    onClick={() => handleSelect(quickTime.value)}
                    disabled={min && quickTime.value < min}
                    style={{
                      padding: isMobile ? '5px 10px' : '6px 12px',
                      background: value === quickTime.value 
                        ? 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)' 
                        : '#fff',
                      color: value === quickTime.value ? '#fff' : '#374151',
                      border: `2px solid ${value === quickTime.value ? '#ff7a1a' : '#e5e7eb'}`,
                      borderRadius: isMobile ? 6 : 8,
                      fontSize: isMobile ? 11 : 12,
                      fontWeight: 600,
                      cursor: min && quickTime.value < min ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      opacity: min && quickTime.value < min ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!(min && quickTime.value < min) && value !== quickTime.value) {
                        e.currentTarget.style.background = '#fff7ed';
                        e.currentTarget.style.borderColor = '#ff7a1a';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(min && quickTime.value < min) && value !== quickTime.value) {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    {quickTime.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Time Slots List */}
          <div style={{ 
            maxHeight: isMobile ? '50vh' : 280, 
            overflowY: 'auto',
            padding: isMobile ? 2 : 4
          }}>
            {filteredSlots.length === 0 ? (
              <div style={{ 
                padding: isMobile ? 20 : 24, 
                textAlign: 'center', 
                color: '#9ca3af',
                fontSize: isMobile ? 13 : 14
              }}>
                {isArabic ? 'لا توجد نتائج' : 'No results found'}
              </div>
            ) : (
              filteredSlots.map((slot) => (
                <button
                  key={slot.time24}
                  type="button"
                  onClick={() => handleSelect(slot.time24)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px 14px' : '12px 16px',
                    background: value === slot.time24 
                      ? 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)' 
                      : 'transparent',
                    color: value === slot.time24 ? '#fff' : '#374151',
                    border: 'none',
                    borderRadius: isMobile ? 6 : 8,
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: value === slot.time24 ? 700 : 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onMouseEnter={(e) => {
                    if (value !== slot.time24) {
                      e.currentTarget.style.background = '#fff7ed';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (value !== slot.time24) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span>{slot.time12}</span>
                  {value === slot.time24 && (
                    <FaCheck size={isMobile ? 14 : 16} style={{ color: '#22c55e' }} />
                  )}
                </button>
              ))
            )}
          </div>
          {/* Close button for mobile */}
          {isMobile && (
            <div style={{ 
              padding: 12, 
              borderTop: '1px solid #e5e7eb',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                style={{
                  padding: '10px 24px',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#6b7280';
                }}
              >
                {isArabic ? 'إغلاق' : 'Close'}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Backdrop for mobile */}
      {isOpen && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Feedback/Support Form Component
const FeedbackForm = ({ isArabic, profile }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Update form data when profile changes
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        name: profile.name || '',
        phone: profile.phone || '',
        email: profile.email || ''
      }));
    }
  }, [profile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus({ type: 'error', text: isArabic ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields' });
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/send-teacher-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone || 'Not provided',
          email: formData.email,
          message: formData.message,
          teacherEmail: profile?.email || formData.email
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ type: 'success', text: isArabic ? 'تم إرسال رسالتك بنجاح! سنتواصل معك قريباً.' : 'Your message has been sent successfully! We will contact you soon.' });
        setFormData({ ...formData, message: '' });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      setSubmitStatus({ type: 'error', text: isArabic ? 'حدث خطأ أثناء إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'An error occurred while sending your message. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      background: '#f8fafc',
      borderRadius: 16,
      padding: 24,
      border: '2px solid #e5e7eb',
      marginBottom: 24
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <FaComments size={24} color="#FF7426" />
        <h3 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>
          {isArabic ? 'اتصل بنا / أبلغ عن مشكلة' : 'Contact Us / Report an Issue'}
        </h3>
      </div>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
        {isArabic 
          ? 'هل لديك سؤال أو اقتراح أو مشكلة؟ نود أن نسمع منك!'
          : 'Have a question, suggestion, or issue? We\'d love to hear from you!'}
      </p>
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {isArabic ? 'الاسم الكامل' : 'Full Name'} *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                fontSize: 14,
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={e => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {isArabic ? 'رقم الهاتف' : 'Phone Number'}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                fontSize: 14,
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={e => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {isArabic ? 'البريد الإلكتروني' : 'Email'} *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 8,
              border: '2px solid #e5e7eb',
              fontSize: 14,
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {isArabic ? 'رسالتك / المشكلة / الاقتراح' : 'Your Message / Issue / Recommendation'} *
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            placeholder={isArabic ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 8,
              border: '2px solid #e5e7eb',
              fontSize: 14,
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              transition: 'all 0.2s'
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>
        
        {submitStatus && (
          <div style={{
            padding: 12,
            borderRadius: 8,
            background: submitStatus.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: submitStatus.type === 'success' ? '#065f46' : '#991b1b',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            {submitStatus.type === 'success' ? <FaCheckCircle size={16} style={{ color: '#16a34a' }} /> : <FaTimesCircle size={16} style={{ color: '#dc2626' }} />}
            <span>{submitStatus.text}</span>
          </div>
        )}
        
        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: '14px 24px',
            background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 700,
            cursor: submitting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            transition: 'all 0.2s',
            boxShadow: submitting ? 'none' : '0 4px 12px rgba(59,130,246,0.3)'
          }}
          onMouseOver={e => {
            if (!submitting) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59,130,246,0.4)';
            }
          }}
          onMouseOut={e => {
            if (!submitting) {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.3)';
            }
          }}
        >
          {submitting ? (
            <>
              <FaHourglassHalf size={16} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
              <span>{isArabic ? 'جاري الإرسال...' : 'Sending...'}</span>
            </>
          ) : (
            <>
              <FaEnvelope size={16} />
              <span>{isArabic ? 'إرسال الرسالة' : 'Send Message'}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}

const bahrainSchoolsAndUniversities = [
  "University of Bahrain",
  "Arabian Gulf University",
  "Ahlia University",
  "Bahrain Polytechnic",
  "Royal University for Women",
  "Applied Science University",
  "Gulf University",
  "AMA International University",
  "Bahrain Institute of Banking and Finance",
  "Al Hekma International School",
  "St. Christopher's School",
  "The Indian School",
  "Bahrain Bayan School",
  "Naseem International School",
  "Al Noor International School",
  "New Millennium School",
  "British School of Bahrain",
  "Shaikha Hessa Girls' School",
  "Al Wisam School",
  "Al Raja School",
  "Al Rawabi Private School",
  "Al Mahd Day Boarding School",
  "Al Salam School",
  "Al Shorooq International School",
  "Al-Amal School",
  "Al-Ibdaa School",
  "Al-Mawakeb School",
  "Al-Muntazah School",
  "Al-Taqwa School",
  "Al-Waha School",
  "Al-Wurood International School",
  "Al-Zahra School",
  "Beacon Private School",
  "Capital School Bahrain",
  "City International School",
  "Diyar International Private School",
  "Dream International School",
  "Future Generation Private School",
  "GEMS International School",
  "Indian School Bahrain",
  "Iqra'a Bilingual School",
  "Khalil Bin Ahmed School",
  "Kuwait National English School",
  "New Indian School",
  "NIVA International School",
  "Pakistan Urdu School",
  "Riffa Views International School",
  "Saar International School",
  "Sacred Heart School",
  "St. Michael's School",
  "The Asian School",
  "The British School of Bahrain",
  "The French School of Bahrain",
  "The Japanese School in Bahrain",
  "The New Horizon School",
  "The Philippine School Bahrain",
  "The Sheikh Khalifa Bin Salman Institute of Technology",
  "The Westminster School",
  "Nasser Centre for Science and Technology",
  "Abdulla Bin Salman Primary Boys School",
  "Abdulla Bin Salman Primary Girls School",
  "Abu Saiba Primary Boys School",
  "Abu Saiba Primary Girls School",
  "Adhari Primary Boys School",
  "Adhari Primary Girls School",
  "Al Hidd Secondary Boys School",
  "Al Hidd Secondary Girls School",
  "Al Iman Primary Boys School",
  "Al Iman Primary Girls School",
  "Al Ma'arifa Secondary Boys School",
  "Al Ma'arifa Secondary Girls School",
  "Al Malkiya Primary Boys School",
  "Al Malkiya Primary Girls School",
  "Al Qadam Primary Boys School",
  "Al Qadam Primary Girls School",
  "Al Shurooq Secondary Girls School",
  "Al Wusta Primary Boys School",
  "Al Wusta Primary Girls School",
  "Arad Primary Boys School",
  "Arad Primary Girls School",
  "Bani Jamra Primary Boys School",
  "Bani Jamra Primary Girls School",
  "Budaiya Primary Boys School",
  "Budaiya Primary Girls School",
  "Duraz Primary Boys School",
  "Duraz Primary Girls School",
  "East Riffa Secondary Boys School",
  "East Riffa Secondary Girls School",
  "Isa Town Secondary Boys School",
  "Isa Town Secondary Girls School",
  "Jidhafs Secondary Boys School",
  "Jidhafs Secondary Girls School",
  "Juffair Primary Boys School",
  "Juffair Primary Girls School",
  "Manama Secondary Boys School",
  "Manama Secondary Girls School",
  "Muharraq Secondary Boys School",
  "Muharraq Secondary Girls School",
  "Riffa Secondary Boys School",
  "Riffa Secondary Girls School",
  "Sitra Secondary Boys School",
  "Sitra Secondary Girls School",
  "Tubli Primary Boys School",
  "Tubli Primary Girls School",
  "Other"
];

const schoolGrades = Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`);
const universityGrades = ['Foundation', 'Diploma', 'Bachelor', 'Master', 'PhD'];
const universityNames = [
  "University of Bahrain",
  "Arabian Gulf University",
  "Ahlia University",
  "Bahrain Polytechnic",
  "Royal University for Women",
  "Applied Science University",
  "Gulf University",
  "AMA International University",
  // Add more university names as needed
];

// Categorized subjects structure
const subjectsCategories = {
  'Math': ['Mathematics', 'Algebra', 'Geometry', 'Calculus', 'Statistics', 'Pre-Calculus', 'Linear Algebra', 'Discrete Mathematics', 'Differential Equations', 'Probability'],
  'Science': ['General Science', 'Biology', 'Chemistry', 'Physics', 'Environmental Science', 'Earth Science', 'Advanced Physics', 'Advanced Chemistry', 'Advanced Biology', 'Molecular Biology', 'Genetics', 'Geology', 'Astronomy', 'Marine Science', 'Agricultural Science'],
  'English': ['English Literature', 'English Language', 'Writing', 'Grammar', 'Reading'],
  'Arabic': ['Arabic Language', 'Arabic Literature', 'Quran Studies', 'Islamic Studies'],
  'Social Studies': ['History', 'World History', 'Bahrain History', 'Social Studies', 'Civics', 'Geography'],
  'Computer Science': ['ICT', 'Information Technology', 'Programming', 'Coding', 'Data Science', 'Artificial Intelligence', 'Machine Learning', 'Robotics', 'Cybersecurity'],
  'Physical Education': ['PE', 'Sports', 'Health'],
  'Arts': ['Art', 'Fine Arts', 'Drawing', 'Painting', 'Design', 'Graphic Design', 'Interior Design'],
  'Performing Arts': ['Music', 'Drama', 'Theater'],
  'Languages': ['French', 'Spanish', 'German', 'Italian', 'Chinese', 'Japanese', 'Korean', 'Turkish', 'Hindi', 'Urdu', 'Linguistics', 'Translation', 'Literature', 'Comparative Literature'],
  'Business & Economics': ['Economics', 'Business', 'Business Studies', 'Accounting', 'Finance', 'Marketing', 'Management', 'Entrepreneurship'],
  'Engineering': ['Mechanical Engineering', 'Electrical Engineering', 'Civil Engineering', 'Chemical Engineering', 'Computer Engineering', 'Software Engineering', 'Biomedical Engineering', 'Environmental Engineering'],
  'Medicine & Health': ['Medicine', 'Nursing', 'Pharmacy', 'Dentistry', 'Veterinary Medicine', 'Public Health', 'Nutrition'],
  'Law & Politics': ['Law', 'Political Science', 'International Relations', 'Criminal Justice'],
  'Social Sciences': ['Psychology', 'Sociology', 'Anthropology', 'Philosophy', 'Education', 'Pedagogy'],
  'Architecture & Design': ['Architecture', 'Urban Planning'],
  'Media & Communication': ['Media Studies', 'Journalism', 'Communication'],
  'Hospitality & Tourism': ['Hospitality', 'Tourism', 'Hotel Management', 'Culinary Arts'],
  'Other': []
};

// Flattened subjects list for backward compatibility
const subjects = Object.entries(subjectsCategories).flatMap(([category, items]) => 
  items.length > 0 ? [category, ...items] : [category]
);
const languages = [
  'Arabic', 'English', 'French', 'German', 'Spanish', 'Hindi', 'Urdu', 'Bengali', 'Chinese', 'Russian', 'Other'
];
const priceOptions = Array.from({ length: 18 }, (_, i) => `${i + 3} BD`);

// Arabic categorized subjects structure
const subjectsCategoriesAr = {
  'الرياضيات': ['الرياضيات العامة', 'الجبر', 'الهندسة', 'التفاضل والتكامل', 'الإحصاء', 'ما قبل التفاضل', 'الجبر الخطي', 'الرياضيات المتقطعة', 'المعادلات التفاضلية', 'الاحتمالات'],
  'العلوم': ['العلوم العامة', 'الأحياء', 'الكيمياء', 'الفيزياء', 'العلوم البيئية', 'علوم الأرض', 'الفيزياء المتقدمة', 'الكيمياء المتقدمة', 'الأحياء المتقدمة', 'الأحياء الجزيئية', 'علم الوراثة', 'الجيولوجيا', 'علم الفلك', 'العلوم البحرية', 'العلوم الزراعية'],
  'الإنجليزية': ['الأدب الإنجليزي', 'اللغة الإنجليزية', 'الكتابة', 'القواعد', 'القراءة'],
  'العربية': ['اللغة العربية', 'الأدب العربي', 'القرآن الكريم', 'التربية الإسلامية'],
  'الدراسات الاجتماعية': ['التاريخ', 'التاريخ العالمي', 'تاريخ البحرين', 'الدراسات الاجتماعية', 'التربية المدنية', 'الجغرافيا'],
  'علوم الحاسوب': ['تكنولوجيا المعلومات', 'البرمجة', 'الترميز', 'علوم البيانات', 'الذكاء الاصطناعي', 'التعلم الآلي', 'الروبوتات', 'الأمن السيبراني'],
  'التربية البدنية': ['الرياضة', 'الصحة'],
  'الفنون': ['الفنون العامة', 'الرسم', 'اللوحة', 'التصميم', 'التصميم الجرافيكي', 'التصميم الداخلي'],
  'الفنون الأدائية': ['الموسيقى', 'المسرح', 'الدراما'],
  'اللغات': ['الفرنسية', 'الإسبانية', 'الألمانية', 'الإيطالية', 'الصينية', 'اليابانية', 'الكورية', 'التركية', 'الهندية', 'الأردية', 'اللغويات', 'الترجمة', 'الأدب', 'الأدب المقارن'],
  'الأعمال والاقتصاد': ['الاقتصاد', 'إدارة الأعمال', 'المحاسبة', 'المالية', 'التسويق', 'الإدارة', 'ريادة الأعمال'],
  'الهندسة': ['الهندسة الميكانيكية', 'الهندسة الكهربائية', 'الهندسة المدنية', 'الهندسة الكيميائية', 'هندسة الحاسوب', 'هندسة البرمجيات', 'الهندسة الطبية الحيوية', 'الهندسة البيئية'],
  'الطب والصحة': ['الطب', 'التمريض', 'الصيدلة', 'طب الأسنان', 'الطب البيطري', 'الصحة العامة', 'التغذية'],
  'القانون والسياسة': ['القانون', 'العلوم السياسية', 'العلاقات الدولية', 'العدالة الجنائية'],
  'العلوم الاجتماعية': ['علم النفس', 'علم الاجتماع', 'الأنثروبولوجيا', 'الفلسفة', 'التربية', 'التربية والتعليم'],
  'العمارة والتصميم': ['العمارة', 'التخطيط الحضري'],
  'الإعلام والاتصال': ['الدراسات الإعلامية', 'الصحافة', 'الاتصال'],
  'الضيافة والسياحة': ['الضيافة', 'السياحة', 'إدارة الفنادق', 'الفنون الطهوية'],
  'أخرى': []
};

// Flattened Arabic subjects list for backward compatibility
const subjectsAr = Object.entries(subjectsCategoriesAr).flatMap(([category, items]) => 
  items.length > 0 ? [category, ...items] : [category]
);
const languagesAr = [
  'العربية', 'الإنجليزية', 'الفرنسية', 'الألمانية', 'الإسبانية', 'الهندية', 'الأردية', 'البنغالية', 'الصينية', 'الروسية', 'أخرى'
];
const schoolGradesAr = Array.from({ length: 12 }, (_, i) => `الصف ${i + 1}`);
const universityGradesAr = ['سنة تأسيسية', 'دبلوم', 'بكالوريوس', 'ماجستير', 'دكتوراه'];

// Custom MultiSelectDropdown component
function MultiSelectDropdown({ label, options, selected, setSelected, maxSelect, placeholder, single }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const listRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'Tab') setOpen(false);
      if (e.key === 'ArrowDown' && listRef.current) {
        const first = listRef.current.querySelector('input');
        if (first) first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleToggle = () => setOpen((o) => !o);
  const handleSelect = (option) => {
    if (single) {
      setSelected([option]);
      setOpen(false);
    } else if (selected.includes(option)) {
      setSelected(selected.filter((s) => s !== option));
    } else if (!maxSelect || selected.length < maxSelect) {
      setSelected([...selected, option]);
    }
  };
  const handleClear = (e) => {
    e.stopPropagation();
    setSelected([]);
  };
  const display = selected.length > 0 ? selected.join(', ') : placeholder || 'Select...';
  const isMax = maxSelect && selected.length >= maxSelect;
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ fontWeight: 700, fontSize: 17, marginBottom: 2, display: 'block' }}>{label}</label>
      <div ref={ref} style={{ position: 'relative' }}>
        <div
          onClick={handleToggle}
          tabIndex={0}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleToggle()}
          style={{
            width: '100%',
            maxWidth: 280,
            minHeight: 32,
            padding: '6px 12px',
            borderRadius: 24,
            background: '#dedede',
            color: selected.length > 0 ? '#232B3E' : '#888',
            fontWeight: selected.length > 0 ? 700 : 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            border: 'none',
            marginLeft: 0,
            marginBottom: 0,
            outline: open ? '2px solid #FF7426' : 'none',
            transition: 'outline 0.2s',
          }}
        >
          <span style={{ flex: 1, textAlign: 'left', color: selected.length > 0 ? '#232B3E' : '#888', fontWeight: selected.length > 0 ? 700 : 500, fontSize: 14 }}>
            {display}
          </span>
          {selected.length > 0 && (
            <button
              type="button"
              tabIndex={-1}
              onClick={handleClear}
              style={{ background: 'none', border: 'none', color: '#FF7426', fontSize: 14, marginRight: 8, cursor: 'pointer' }}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          <span style={{ display: 'flex', alignItems: 'center', height: 24 }}>
            <FaChevronDown style={{ color: '#FF7426', fontSize: 20, marginLeft: 8, verticalAlign: 'middle' }} />
          </span>
        </div>
        {open && (
          <div
            ref={listRef}
            style={{
              position: 'absolute',
              top: '110%',
              left: 0,
              width: '100%',
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 4px 24px #e0eaff',
              zIndex: 1000,
              padding: 12,
              maxHeight: 260,
              overflowY: 'auto',
              border: '1.5px solid #FF7426',
              marginTop: 4,
            }}
          >
            {options.map((option, idx) => (
              <label key={option} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', cursor: isMax && !selected.includes(option) ? 'not-allowed' : 'pointer', fontSize: 16, opacity: isMax && !selected.includes(option) ? 0.5 : 1 }}>
                <input
                  type={single ? 'radio' : 'checkbox'}
                  checked={selected.includes(option)}
                  onChange={() => handleSelect(option)}
                  style={{ marginRight: 14, width: 18, height: 18 }}
                  disabled={isMax && !selected.includes(option)}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') handleSelect(option);
                  }}
                />
                {option}
              </label>
            ))}
            {maxSelect && !single && (
              <div style={{ fontSize: 13, color: isMax ? '#FF7426' : '#888', marginTop: 8, textAlign: 'right' }}>
                {selected.length} / {maxSelect} selected
                {isMax && ' (max reached)'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Categorized MultiSelectDropdown component for subjects
function CategorizedMultiSelectDropdown({ label, categories, selected, setSelected, maxSelect, placeholder, isArabic }) {
  const [open, setOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const ref = useRef();
  const listRef = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'Tab') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleToggle = () => setOpen((o) => !o);
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSelect = (subject) => {
    if (selected.includes(subject)) {
      setSelected(selected.filter((s) => s !== subject));
    } else if (!maxSelect || selected.length < maxSelect) {
      setSelected([...selected, subject]);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected([]);
  };

  // Get all selected subjects (including sub-subjects)
  const getAllSelectedSubjects = () => {
    const all = [];
    Object.entries(categories).forEach(([category, items]) => {
      if (selected.includes(category)) {
        all.push(category);
      }
      items.forEach(item => {
        if (selected.includes(item)) {
          all.push(item);
        }
      });
    });
    return all;
  };

  const display = getAllSelectedSubjects().length > 0 
    ? getAllSelectedSubjects().join(', ') 
    : placeholder || 'Select...';
  const isMax = maxSelect && getAllSelectedSubjects().length >= maxSelect;

  // Flatten all subjects for checking
  const allSubjects = Object.entries(categories).flatMap(([category, items]) => 
    items.length > 0 ? [category, ...items] : [category]
  );

  return (
    <div style={{ marginBottom: 18 }}>
      <div ref={ref} style={{ position: 'relative' }}>
        <div
          onClick={handleToggle}
          tabIndex={0}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleToggle()}
          style={{
            width: '100%',
            maxWidth: 280,
            minHeight: 32,
            padding: '6px 12px',
            borderRadius: 24,
            background: '#dedede',
            color: getAllSelectedSubjects().length > 0 ? '#232B3E' : '#888',
            fontWeight: getAllSelectedSubjects().length > 0 ? 700 : 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            border: 'none',
            marginLeft: 0,
            marginBottom: 0,
            outline: open ? '2px solid #FF7426' : 'none',
            transition: 'outline 0.2s',
          }}
        >
          <span style={{ flex: 1, textAlign: 'left', color: getAllSelectedSubjects().length > 0 ? '#232B3E' : '#888', fontWeight: getAllSelectedSubjects().length > 0 ? 700 : 500, fontSize: 14 }}>
            {display}
          </span>
          {getAllSelectedSubjects().length > 0 && (
            <button
              type="button"
              tabIndex={-1}
              onClick={handleClear}
              style={{ background: 'none', border: 'none', color: '#FF7426', fontSize: 14, marginRight: 8, cursor: 'pointer' }}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          <span style={{ display: 'flex', alignItems: 'center', height: 24 }}>
            <FaChevronDown style={{ color: '#FF7426', fontSize: 20, marginLeft: 8, verticalAlign: 'middle' }} />
          </span>
        </div>
        {open && (
          <div
            ref={listRef}
            style={{
              position: 'absolute',
              top: '110%',
              left: 0,
              width: '100%',
              minWidth: 300,
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 4px 24px #e0eaff',
              zIndex: 1000,
              padding: 12,
              maxHeight: 400,
              overflowY: 'auto',
              border: '1.5px solid #FF7426',
              marginTop: 4,
            }}
          >
            {Object.entries(categories).map(([category, items]) => {
              const hasSubItems = items.length > 0;
              const isExpanded = expandedCategories[category];
              const categorySelected = selected.includes(category);
              const hasSelectedSubItems = items.some(item => selected.includes(item));
              
              return (
                <div key={category} style={{ marginBottom: 4 }}>
                  {/* Category Header */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: hasSubItems && isExpanded ? 4 : 0 }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '8px 0', 
                      cursor: isMax && !categorySelected && !hasSelectedSubItems ? 'not-allowed' : 'pointer', 
                      fontSize: 16, 
                      fontWeight: 600,
                      opacity: isMax && !categorySelected && !hasSelectedSubItems ? 0.5 : 1,
                      flex: 1
                    }}>
                      <input
                        type="checkbox"
                        checked={categorySelected}
                        onChange={() => handleSelect(category)}
                        style={{ marginRight: 12, width: 18, height: 18 }}
                        disabled={isMax && !categorySelected && !hasSelectedSubItems}
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelect(category);
                          }
                        }}
                      />
                      <span style={{ color: categorySelected ? '#FF7426' : '#333' }}>{category}</span>
                    </label>
                    {hasSubItems && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(category);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          color: '#FF7426',
                          fontSize: 14,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <FaChevronDown style={{ 
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }} />
                      </button>
                    )}
                  </div>
                  
                  {/* Sub-items */}
                  {hasSubItems && isExpanded && (
                    <div style={{ marginLeft: 30, marginTop: 4 }}>
                      {items.map((item) => {
                        const itemSelected = selected.includes(item);
                        return (
                          <label 
                            key={item} 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '6px 0', 
                              cursor: isMax && !itemSelected ? 'not-allowed' : 'pointer', 
                              fontSize: 15, 
                              opacity: isMax && !itemSelected ? 0.5 : 1 
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={itemSelected}
                              onChange={() => handleSelect(item)}
                              style={{ marginRight: 12, width: 16, height: 16 }}
                              disabled={isMax && !itemSelected}
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleSelect(item);
                                }
                              }}
                            />
                            <span style={{ color: itemSelected ? '#FF7426' : '#666' }}>{item}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {maxSelect && (
              <div style={{ fontSize: 13, color: isMax ? '#FF7426' : '#888', marginTop: 12, textAlign: 'right', paddingTop: 8, borderTop: '1px solid #e9ecef' }}>
                {getAllSelectedSubjects().length} / {maxSelect} selected
                {isMax && ' (max reached)'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper for file type icon
const FileIcon = ({ url }) => {
  if (!url || typeof url !== 'string') return <FaFileAlt size={28} style={{ color: '#888', marginRight: 8 }} title="File" />;
  const isImage = url.match(/\.(jpg|jpeg|png|gif)$/i);
  const isPDF = url.match(/\.pdf$/i);
  if (isImage) return <img src={url} alt="preview" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, marginRight: 8 }} />;
  if (isPDF) return <FaFileAlt size={28} style={{ color: '#FF7426', marginRight: 8 }} title="PDF" />;
  return <FaFileAlt size={28} style={{ color: '#888', marginRight: 8 }} title="File" />;
};

// Custom Date Picker Component
const DatePicker = ({ value, onChange, isArabic }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const containerRef = useRef(null);
  const yearPickerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const months = isArabic 
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const weekDays = isArabic 
    ? ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س']
    : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (yearPickerRef.current && !yearPickerRef.current.contains(event.target)) {
        setShowYearPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate years list (from 1950 to current year + 1)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 1950; year <= currentYear + 1; year++) {
    years.push(year);
  }
  years.reverse(); // Most recent years first

  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
    }
  }, [value]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday = 0
    
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getNextMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    onChange(formattedDate);
    setIsOpen(false);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const month1 = currentMonth;
  const days1 = getDaysInMonth(month1);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        readOnly
        value={formatDisplayDate(value)}
        onClick={() => setIsOpen(!isOpen)}
        placeholder={isArabic ? 'اختر التاريخ' : 'Select Date'}
        style={{
          width: '100%',
          padding: 8,
          borderRadius: 8,
          border: '1.5px solid #e0eaff',
          fontSize: 15,
          background: '#f3f6fa',
          color: '#888',
          cursor: 'pointer'
        }}
      />
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: isMobile ? '50%' : 0,
          zIndex: 1000,
          background: '#fff',
          borderRadius: 12,
          padding: 16,
          boxShadow: '0 8px 24px rgba(15, 37, 69, 0.15)',
          marginTop: 6,
          minWidth: isMobile ? 'calc(100vw - 40px)' : 280,
          maxWidth: isMobile ? 'calc(100vw - 40px)' : 280,
          transform: isMobile ? 'translateX(-50%)' : 'none',
          border: '1px solid #e0eaff'
        }}>
          {/* Header with Month/Year */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: 12,
            paddingBottom: 10,
            borderBottom: '2px solid #FF7426',
            position: 'relative'
          }}>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#0F2545',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FDF8EE';
                e.target.style.color = '#FF7426';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#0F2545';
              }}
            >
              <FaChevronLeft size={14} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ color: '#0F2545', fontWeight: 700, fontSize: 14 }}>
                {months[month1.getMonth()]}
              </div>
              <div 
                ref={yearPickerRef}
                style={{ position: 'relative' }}
              >
                <button
                  onClick={() => setShowYearPicker(!showYearPicker)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#0F2545',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: 4,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#FDF8EE';
                    e.target.style.color = '#FF7426';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#0F2545';
                  }}
                >
                  {month1.getFullYear()}
                </button>
                {showYearPicker && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#fff',
                    borderRadius: 8,
                    padding: 6,
                    maxHeight: 180,
                    overflowY: 'auto',
                    zIndex: 1001,
                    marginTop: 6,
                    minWidth: 90,
                    boxShadow: '0 6px 20px rgba(15, 37, 69, 0.15)',
                    border: '1px solid #e0eaff'
                  }}>
                    {years.map((year) => (
                      <div
                        key={year}
                        onClick={() => {
                          setCurrentMonth(new Date(year, month1.getMonth(), 1));
                          setShowYearPicker(false);
                        }}
                        style={{
                          color: year === month1.getFullYear() ? '#FF7426' : '#0F2545',
                          fontSize: 13,
                          padding: '6px 10px',
                          cursor: 'pointer',
                          borderRadius: 4,
                          background: year === month1.getFullYear() ? '#FDF8EE' : 'transparent',
                          textAlign: 'center',
                          fontWeight: year === month1.getFullYear() ? 700 : 500,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (year !== month1.getFullYear()) {
                            e.target.style.background = '#FDF8EE';
                            e.target.style.color = '#FF7426';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (year !== month1.getFullYear()) {
                            e.target.style.background = 'transparent';
                            e.target.style.color = '#0F2545';
                          }
                        }}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#0F2545',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FDF8EE';
                e.target.style.color = '#FF7426';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#0F2545';
              }}
            >
              <FaChevronRight size={14} />
            </button>
          </div>

          {/* Week Days Header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 8 }}>
            {weekDays.map((day, idx) => (
              <div key={idx} style={{ 
                color: '#0F2545', 
                fontSize: 11, 
                fontWeight: 700, 
                textAlign: 'center', 
                padding: 4,
                opacity: 0.7
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {days1.map((date, idx) => (
              <button
                key={idx}
                onClick={() => date && handleDateSelect(date)}
                disabled={!date}
                style={{
                  background: date && isSameDay(date, selectedDate) 
                    ? '#FF7426' 
                    : 'transparent',
                  border: date 
                    ? date && isSameDay(date, selectedDate)
                      ? '2px solid #FF7426'
                      : '1px solid #e0eaff'
                    : 'none',
                  borderRadius: date ? '50%' : 0,
                  color: date 
                    ? (date && isSameDay(date, selectedDate) ? '#fff' : '#0F2545')
                    : 'transparent',
                  fontSize: 13,
                  padding: 6,
                  cursor: date ? 'pointer' : 'default',
                  minHeight: 32,
                  minWidth: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  fontWeight: date && isSameDay(date, selectedDate) ? 700 : 500
                }}
                onMouseEnter={(e) => {
                  if (date && !isSameDay(date, selectedDate)) {
                    e.target.style.background = '#FDF8EE';
                    e.target.style.borderColor = '#FF7426';
                    e.target.style.color = '#FF7426';
                  }
                }}
                onMouseLeave={(e) => {
                  if (date && !isSameDay(date, selectedDate)) {
                    e.target.style.background = 'transparent';
                    e.target.style.borderColor = '#e0eaff';
                    e.target.style.color = '#0F2545';
                  }
                }}
              >
                {date ? date.getDate() : ''}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: 16, 
            paddingTop: 12, 
            borderTop: '1px solid #e0eaff' 
          }}>
            <button
              onClick={() => {
                setSelectedDate(null);
                onChange('');
                setIsOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#0F2545',
                cursor: 'pointer',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 6,
                transition: 'all 0.2s',
                opacity: 0.7
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '1';
                e.target.style.color = '#FF7426';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '0.7';
                e.target.style.color = '#0F2545';
              }}
            >
              {isArabic ? 'مسح' : 'Clear'}
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'transparent',
                  border: '1.5px solid #e0eaff',
                  color: '#0F2545',
                  cursor: 'pointer',
                  padding: '6px 16px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#FF7426';
                  e.target.style.color = '#FF7426';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e0eaff';
                  e.target.style.color = '#0F2545';
                }}
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: '#FF7426',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: '6px 16px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 6px rgba(255, 116, 38, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#FF8C42';
                  e.target.style.boxShadow = '0 3px 10px rgba(255, 116, 38, 0.4)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FF7426';
                  e.target.style.boxShadow = '0 2px 6px rgba(255, 116, 38, 0.3)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {isArabic ? 'تطبيق' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function isAtLeast18YearsOld(dob) {
  if (!dob) return false;
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 18;
}

export default function TeacherProfile() {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    grade: '',
    image: null,
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    schoolOther: '',
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [introVideoUrl, setIntroVideoUrl] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  // Image cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Always call useOutletContext at the top level, unconditionally
  const outletContext = useOutletContext() || {};
  const sidebarVisible = outletContext.sidebarVisible;
  const setSidebarVisible = typeof outletContext.setSidebarVisible === 'function' ? outletContext.setSidebarVisible : () => {};
  const isArabic = !!outletContext.isArabic;
  const refreshTeacherProfile = typeof outletContext.refreshTeacherProfile === 'function' ? outletContext.refreshTeacherProfile : null;

  const t = isArabic ? {
    profile: 'الملف الشخصي',
    subtitle: 'ملفك هو مفتاحك الرئيسي للتواصل مع الطلاب.',
    completeness: 'اكتمال الملف',
    tip: 'نصيحة: أكمل جميع الأقسام للحصول على فرصة أفضل للاختيار من قبل الطلاب!',
    personalInfo: 'المعلومات الشخصية',
    name: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    phone: 'رقم الهاتف',
    dob: 'تاريخ الميلاد',
    imageHelp: 'اضغط على الصورة لتحميل أو تغيير صورتك. JPG أو PNG أو GIF. الحد الأقصى 5MB.',
    completenessUnit: '% مكتمل',
    checklist: {
      name: 'الاسم', email: 'البريد الإلكتروني', phone: 'الهاتف', dob: 'تاريخ الميلاد', bio: 'نبذة', subjects: 'المواد', grades: 'المستويات', languages: 'اللغات', price: 'السعر للساعة', qualifications: 'المؤهلات'
    },
    qualifications: 'المؤهلات (اختياري ولكن يُنصح به)',
    dragBrowse: 'اسحب وأفلت المزيد من الملفات أو تصفح',
    qualHelp: 'قم برفع الشهادات أو الدرجات أو أي إثبات آخر. PDF أو صورة، حد أقصى 5MB لكل ملف. اختياري ولكن يُنصح به لبناء الثقة مع الطلاب.',
    bio: 'نبذة',
    bioHint: 'صف أسلوبك وخبرتك التعليمية بإيجاز. بحد أقصى 23 كلمة.',
    teachingDetails: 'تفاصيل التدريس',
    subjectsYouTeach: 'المواد التي تقوم بتدريسها',
    gradesYouTeach: 'المستويات التي تقوم بتدريسها',
    languages: 'اللغات',
    pricePerHour: 'السعر للساعة',
    pricePerHourGroup: 'السعر للساعة (للمجموعة)',
    priceNote: 'ملاحظة: ستقوم الشركة بخصم 2 دينار لكل ساعة من سعرك.',
    changePassword: 'تغيير كلمة المرور',
    oldPassword: 'كلمة المرور القديمة',
    confirmPassword: 'تأكيد كلمة المرور',
    newPassword: 'كلمة المرور الجديدة',
    saving: 'يتم الحفظ...',
    changePasswordBtn: 'تغيير كلمة المرور',
    logout: 'تسجيل الخروج',
    updateChanges: 'حفظ التغييرات',
  } : {
    profile: 'Profile',
    subtitle: 'Your profile is your main key to get Teachers.',
    completeness: 'Profile Completeness',
    tip: 'Tip: Complete all sections for a better chance of being selected by students!',
    personalInfo: 'Personal Information',
    name: 'First & Last name',
    email: 'Email',
    phone: 'Phone',
    dob: 'Date of Birth',
    imageHelp: 'Click the image to upload or change your profile photo. JPG, PNG, or GIF. Max 5MB.',
    completenessUnit: '% complete',
    checklist: {
      name: 'Name', email: 'Email', phone: 'Phone', dob: 'Date of Birth', bio: 'Bio', subjects: 'Subjects', grades: 'Grades', languages: 'Languages', price: 'Price Per Hour', qualifications: 'Qualifications'
    },
    qualifications: 'Qualifications (Optional but Recommended)',
    dragBrowse: 'Drag & drop more files or Browse',
    qualHelp: 'Upload certificates, degrees, or other proof. PDF or image, max 5MB each. Optional but highly recommended to build trust with students.',
    bio: 'Bio',
    bioHint: 'Describe your teaching style, experience, or philosophy. Max 23 words.',
    teachingDetails: 'Teaching Details',
    subjectsYouTeach: 'Subjects You Teach',
    gradesYouTeach: 'Grade Levels You Teach',
    languages: 'Languages',
    pricePerHour: 'Price Per Hour',
    pricePerHourGroup: 'Price Per Hour (for Group)',
    priceNote: 'Note: that the company will take 2BD per hour from your price.',
    changePassword: 'Change Password',
    oldPassword: 'Old Password',
    confirmPassword: 'Confirm password',
    newPassword: 'New Password',
    saving: 'Saving...',
    changePasswordBtn: 'Change Password',
    logout: 'Logout',
    updateChanges: 'Update Changes',
  };
  const [dob, setDob] = useState('');
  const [bio, setBio] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedSubjectTypes, setSelectedSubjectTypes] = useState({}); // { Mathematics: ['Algebra', 'Calculus'], ... }
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]); // Array of {language: string, level: string}
  const [experiences, setExperiences] = useState([]); // Array of experience objects
  const [experienceNone, setExperienceNone] = useState(false);
  const [customSubject, setCustomSubject] = useState('');
  const [pricePerHour, setPricePerHour] = useState('');
  const [groupPricePerHour, setGroupPricePerHour] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [pricePerHourArr, setPricePerHourArr] = useState([]);
  const [groupPricePerHourArr, setGroupPricePerHourArr] = useState([]);
  const [groupPricingModel, setGroupPricingModel] = useState('per_person'); // 'fixed' or 'per_person'
  const [qualificationDocs, setQualificationDocs] = useState([]);
  const [qualDragActive, setQualDragActive] = useState(false);
  const [bioError, setBioError] = useState('');
  const [qualificationDocsError, setQualificationDocsError] = useState('');
  const [qualificationDocsSuccess, setQualificationDocsSuccess] = useState('');

  // Banking information state
  const [iban, setIban] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [passwordHash, setPasswordHash] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'profile', 'settings'
  const [profileSubTab, setProfileSubTab] = useState('personal'); // 'personal', 'teaching', 'availability', 'review'
  const [wizardDataForReview, setWizardDataForReview] = useState(null); // Data formatted for Step10Review
  const [performanceTab, setPerformanceTab] = useState('earnings'); // 'earnings' or 'sessions'
  
  // Availability Schedule State
  // Structure: { monday: { allDay: false, notAvailable: false, slots: [{ from: '09:00', to: '12:00' }, ...] }, ... }
  // Three states: 1) allDay: true (available all day), 2) notAvailable: true (not available), 3) slots: [...] (specific times)
  const [availabilitySchedule, setAvailabilitySchedule] = useState({
    monday: { allDay: false, notAvailable: false, slots: [] },
    tuesday: { allDay: false, notAvailable: false, slots: [] },
    wednesday: { allDay: false, notAvailable: false, slots: [] },
    thursday: { allDay: false, notAvailable: false, slots: [] },
    friday: { allDay: false, notAvailable: false, slots: [] },
    saturday: { allDay: false, notAvailable: false, slots: [] },
    sunday: { allDay: false, notAvailable: false, slots: [] }
  });
  
  // Track which days are showing custom times section
  const [showCustomTimes, setShowCustomTimes] = useState({});
  
  // Track which time dropdown is open
  const [openTimeDropdown, setOpenTimeDropdown] = useState(null);

  // Lesson Format Preference State
  const [lessonFormatPreference, setLessonFormatPreference] = useState(null);
  const [showFormatPreferencePrompt, setShowFormatPreferencePrompt] = useState(false);
  const [preferredPlace, setPreferredPlace] = useState('');

  // Teacher Locations State (for Google Maps integration)
  const [teacherLocations, setTeacherLocations] = useState([]);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const [stats, setStats] = useState({
    totalStudents: 0,
    totalSessions: 0,
    upcomingSessions: 0,
    completedSessions: 0,
    totalEarnings: 0,
    pendingRequests: 0,
    averageRating: 0,
    ratingsCount: 0,
    paidSessions: 0,
    pendingPayments: 0,
    unpaidSessions: 0,
    paidAmount: 0,
    processingAmount: 0,
    pendingAmount: 0,
    loyaltyTier: 'Bronze',
    loyaltyBadge: '🥉',
    monthlyEarnings: {},
    monthlySessions: {},
    monthlyNewStudents: {},
    earningsChange: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Fallback demo data if profile is empty
  const fallbackProfile = {
    name: 'abdulla hilal',
    email: 'abdullahilal688@gmail.com',
    phone: '32124114',
    school: 'University of Bahrain',
    grade: 'Foundation',
    image: 'https://randomuser.me/api/portraits/men/1.jpg',
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    schoolOther: '',
  };
  const displayProfile = profile && profile.name ? profile : fallbackProfile;

  useEffect(() => {
    setLoading(true);
    const getUserAndProfile = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      const user = session?.user;
      if (user) {
        const meta = user.user_metadata || user.raw_user_meta_data || {};
        const { data: teacher } = await supabase
          .from('Teachers')
          .select('*')
          .eq('email', user.email)
          .single();
        setProfile({
          name: meta.full_name || teacher?.full_name || 'Not provided',
          email: user.email || teacher?.email || 'Not provided',
          phone: meta.phone || teacher?.phone || 'Not provided',
          school: teacher?.school || '',
          grade: teacher?.grade_levels || '',
          image: teacher?.profile_photo || null,
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          schoolOther: '',
        });
        if (teacher?.profile_photo) {
          setImagePreview(teacher.profile_photo);
        }
        if (teacher?.intro_video_url) {
          setIntroVideoUrl(teacher.intro_video_url);
        } else {
          setIntroVideoUrl(null);
        }
        setDob(teacher?.dob || '');
        setBio(teacher?.bio || '');
        const teacherSubjects = Array.isArray(teacher?.subjects) ? teacher.subjects : [];
        setSelectedSubjects(teacherSubjects);
        // Load subject types/specializations
        setSelectedSubjectTypes(teacher?.subject_types || {});
        // Extract custom subject if exists
        const customSubj = teacherSubjects.find(s => s && typeof s === 'string' && s.startsWith('Custom: '));
        if (customSubj) {
          setCustomSubject(customSubj.replace('Custom: ', ''));
        } else {
          setCustomSubject('');
        }
        setSelectedGrades(Array.isArray(teacher?.grade_levels) ? teacher.grade_levels : []);
        // Load languages - ensure they're in the correct format
        const loadedLanguages = Array.isArray(teacher?.languages) 
          ? teacher.languages.map(lang => {
              // If it's already an object with language and level, use it
              if (typeof lang === 'object' && lang !== null && lang.language) {
                return { language: String(lang.language), level: String(lang.level || 'Fluent') };
              }
              // If it's a string, try to parse as JSON first
              if (typeof lang === 'string') {
                try {
                  const parsed = JSON.parse(lang);
                  if (parsed && parsed.language) {
                    return { language: String(parsed.language), level: String(parsed.level || 'Fluent') };
                  }
                } catch (e) {
                  // Not JSON, treat as language name only
                  return { language: lang, level: 'Fluent' };
                }
              }
              return null;
            }).filter(Boolean)
          : [];
        setSelectedLanguages(loadedLanguages);
        
        // Load experiences from most recent submission_data (experiences are not stored in Teachers table)
        const { data: submissionData } = await supabase
          .from('teacher_onboarding_submissions')
          .select('submission_data')
          .eq('teacher_id', user.id)
          .order('submitted_at', { ascending: false })
          .limit(1)
          .single();
        
        if (submissionData?.submission_data?.experiences) {
          setExperiences(submissionData.submission_data.experiences);
          setExperienceNone(submissionData.submission_data.experience_none || false);
        } else {
          // Initialize with one empty experience if none exist
          setExperiences([{ job_title: '', organization: '', from_month: '', from_year: '', to_month: '', to_year: '', present: false }]);
          setExperienceNone(false);
        }
        setPricePerHourArr(Array.isArray(teacher?.hourly_rate) ? teacher.hourly_rate : (teacher?.hourly_rate ? [teacher.hourly_rate] : []));
        setGroupPricePerHourArr(Array.isArray(teacher?.group_hourly_rate) ? teacher.group_hourly_rate : (teacher?.group_hourly_rate ? [teacher.group_hourly_rate] : []));
        setGroupPricingModel(teacher?.group_pricing_model || 'per_person');
        setQualificationDocs(Array.isArray(teacher?.qualifications) ? teacher.qualifications.map(q => ({ url: q, name: '' })) : []);
        setPasswordHash(teacher?.password_hash || '');

        // Load banking information
        setIban(teacher?.iban || '');
        setBankName(teacher?.bank_name || '');
        setAccountHolderName(teacher?.account_holder_name || '');
        setPaymentMethod(teacher?.payment_method || '');

        // Load availability schedule
        if (teacher?.availability_schedule && typeof teacher.availability_schedule === 'object') {
          // Migrate old format (array of slots) to new format (object with allDay, notAvailable, and slots)
          const migratedSchedule = {};
          ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
            const dayData = teacher.availability_schedule[day];
            if (dayData && typeof dayData === 'object' && ('allDay' in dayData || 'notAvailable' in dayData)) {
              // New format
              migratedSchedule[day] = {
                allDay: dayData.allDay || false,
                notAvailable: dayData.notAvailable || false,
                slots: Array.isArray(dayData.slots) ? dayData.slots : []
              };
            } else if (Array.isArray(dayData)) {
              // Old format - migrate to new format
              migratedSchedule[day] = { allDay: false, notAvailable: false, slots: dayData };
            } else {
              migratedSchedule[day] = { allDay: false, notAvailable: false, slots: [] };
            }
          });
          setAvailabilitySchedule(migratedSchedule);
        } else {
          setAvailabilitySchedule({
            monday: { allDay: false, notAvailable: false, slots: [] },
            tuesday: { allDay: false, notAvailable: false, slots: [] },
            wednesday: { allDay: false, notAvailable: false, slots: [] },
            thursday: { allDay: false, notAvailable: false, slots: [] },
            friday: { allDay: false, notAvailable: false, slots: [] },
            saturday: { allDay: false, notAvailable: false, slots: [] },
            sunday: { allDay: false, notAvailable: false, slots: [] }
          });
        }

        // Load lesson format preference
        setLessonFormatPreference(teacher?.lesson_format_preference || null);

        // Load preferred place
        setPreferredPlace(teacher?.preferred_place || '');

        // Show prompt if not set
        if (!teacher?.lesson_format_preference) {
          setShowFormatPreferencePrompt(true);
        }

        // Convert teacher data to wizard format for Step10Review
        const wizardFormatData = {
          full_name: teacher?.full_name || meta.full_name || '',
          email: user.email || teacher?.email || '',
          phone: teacher?.phone || meta.phone || '',
          profile_photo: teacher?.profile_photo || '',
          photo_url: teacher?.profile_photo || '',
          date_of_birth_day: teacher?.dob ? new Date(teacher.dob).getDate().toString() : '',
          date_of_birth_month: teacher?.dob ? new Date(teacher.dob).toLocaleString('en-US', { month: 'long' }) : '',
          date_of_birth_year: teacher?.dob ? new Date(teacher.dob).getFullYear().toString() : '',
          gender: teacher?.gender || '',
          short_bio: teacher?.bio || '',
          bio: teacher?.bio || '',
          subjects: Array.isArray(teacher?.subjects) ? teacher.subjects : (teacher?.subjects ? [teacher.subjects] : []),
          subject: Array.isArray(teacher?.subjects) && teacher.subjects.length > 0 ? teacher.subjects[0] : (teacher?.subjects || ''),
          grade_levels: Array.isArray(teacher?.grade_levels) ? teacher.grade_levels : [],
          languages: Array.isArray(teacher?.languages) 
            ? teacher.languages.map(lang => {
                // If language is already an object with language and level, use it
                if (typeof lang === 'object' && lang !== null && lang.language) {
                  return { language: String(lang.language), level: String(lang.level || 'Fluent') };
                }
                // If it's a string, try to parse as JSON first
                if (typeof lang === 'string') {
                  try {
                    const parsed = JSON.parse(lang);
                    if (parsed && parsed.language) {
                      return { language: String(parsed.language), level: String(parsed.level || 'Fluent') };
                    }
                  } catch (e) {
                    // Not JSON, treat as language name only
                    return { language: lang, level: 'Fluent' };
                  }
                }
                return null;
              }).filter(Boolean)
            : [],
          intro_video_url: teacher?.intro_video_url || '',
          introVideoUrl: teacher?.intro_video_url || '',
          hourly_rate: teacher?.hourly_rate || null,
          group_hourly_rate: teacher?.group_hourly_rate || null,
          group_pricing_model: teacher?.group_pricing_model || 'per_person',
          // Format pricing based on lesson_format_preference
          ...(teacher?.lesson_format_preference === 'both' ? {
            // For 'both', use the same price for both online and face-to-face
            pricing_private_online_per_hour: teacher?.hourly_rate ? `${teacher.hourly_rate} BD` : '',
            pricing_group_online_per_hour: teacher?.group_hourly_rate ? `${teacher.group_hourly_rate} BD` : '',
            pricing_private_face_to_face_per_hour: teacher?.hourly_rate ? `${teacher.hourly_rate} BD` : '',
            pricing_group_face_to_face_per_hour: teacher?.group_hourly_rate ? `${teacher.group_hourly_rate} BD` : '',
            group_pricing_model_online: teacher?.group_pricing_model || 'per_person',
            group_pricing_model_face_to_face: teacher?.group_pricing_model || 'per_person'
          } : {
            // For 'online_only' or 'face_to_face_only', use simple pricing fields
            pricing_private_per_hour: teacher?.hourly_rate ? `${teacher.hourly_rate} BD` : '',
            pricing_group_per_hour: teacher?.group_hourly_rate ? `${teacher.group_hourly_rate} BD` : '',
            group_pricing_model: teacher?.group_pricing_model || 'per_person'
          }),
          qualifications: Array.isArray(teacher?.qualifications) ? teacher.qualifications : [],
          certificates: Array.isArray(teacher?.qualifications) ? teacher.qualifications : [],
          cv_url: teacher?.cv_url || '',
          cvUrl: teacher?.cv_url || '',
          availability_schedule: teacher?.availability_schedule || {},
          availabilitySchedule: teacher?.availability_schedule || {},
          lesson_format_preference: teacher?.lesson_format_preference || '',
          lessonFormatPreference: teacher?.lesson_format_preference || '',
          iban: teacher?.iban || '',
          bank_name: teacher?.bank_name || '',
          account_holder_name: teacher?.account_holder_name || '',
          preferred_place: teacher?.preferred_place || '',
          teaching_locations: [], // Will be updated when teacherLocations loads
          experiences: [], // Will be updated when experiences load
          experience_none: false
        };
        setWizardDataForReview(wizardFormatData);
      } else {
        setProfile({ name: '', email: '', phone: '' });
        setImagePreview(null);
      }
      setLoading(false);
    };
    getUserAndProfile();
  }, []);

  // Listen for edit events from Step10Review component
  useEffect(() => {
    const handleProfileEditStep = (event) => {
      const { subTab, step } = event.detail;
      setProfileSubTab(subTab);
      // Scroll to top of profile section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('profileEditStep', handleProfileEditStep);
    return () => {
      window.removeEventListener('profileEditStep', handleProfileEditStep);
    };
  }, []);

  // Handler for inline profile edits from Step10Review modal
  const handleProfileDataUpdate = async (editedData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user logged in');

      // Build the update object for the Teachers table
      const updateObj = {};

      // Map edited fields to database columns
      if (editedData.full_name !== undefined) updateObj.full_name = editedData.full_name;
      if (editedData.phone !== undefined) updateObj.phone = editedData.phone;
      if (editedData.date_of_birth_day !== undefined) updateObj.date_of_birth_day = editedData.date_of_birth_day;
      if (editedData.date_of_birth_month !== undefined) updateObj.date_of_birth_month = editedData.date_of_birth_month;
      if (editedData.date_of_birth_year !== undefined) updateObj.date_of_birth_year = editedData.date_of_birth_year;
      if (editedData.gender !== undefined) updateObj.gender = editedData.gender;
      if (editedData.short_bio !== undefined) updateObj.short_bio = editedData.short_bio;
      if (editedData.subjects !== undefined) updateObj.subjects = editedData.subjects;
      else if (editedData.subject !== undefined) updateObj.subjects = [editedData.subject];
      if (editedData.subject_types !== undefined) updateObj.subject_types = editedData.subject_types;
      if (editedData.grade_levels !== undefined) updateObj.grade_levels = editedData.grade_levels;
      if (editedData.languages !== undefined) updateObj.languages = editedData.languages;
      if (editedData.experiences !== undefined) updateObj.experiences = editedData.experiences;
      if (editedData.experience_none !== undefined) updateObj.experience_none = editedData.experience_none;
      if (editedData.pricing_private_per_hour !== undefined) updateObj.hourly_rate = parseFloat(editedData.pricing_private_per_hour);
      if (editedData.pricing_group_per_hour !== undefined) updateObj.group_hourly_rate = parseFloat(editedData.pricing_group_per_hour);
      if (editedData.group_pricing_model !== undefined) updateObj.group_pricing_model = editedData.group_pricing_model;
      if (editedData.iban !== undefined) updateObj.iban = editedData.iban;
      if (editedData.bank_name !== undefined) updateObj.bank_name = editedData.bank_name;
      if (editedData.account_holder_name !== undefined) updateObj.account_holder_name = editedData.account_holder_name;
      if (editedData.payment_method !== undefined) updateObj.payment_method = editedData.payment_method;
      if (editedData.availability_schedule !== undefined) updateObj.availability_schedule = editedData.availability_schedule;
      if (editedData.lesson_format_preference !== undefined) updateObj.lesson_format_preference = editedData.lesson_format_preference;
      if (editedData.intro_video_url !== undefined) updateObj.intro_video_url = editedData.intro_video_url;

      // Only update if there are fields to update
      if (Object.keys(updateObj).length > 0) {
        const { error } = await supabase
          .from('Teachers')
          .update(updateObj)
          .eq('email', user.email);

        if (error) throw error;

        // Update local state to reflect changes
        if (editedData.full_name !== undefined) setProfile(prev => ({ ...prev, name: editedData.full_name }));
        if (editedData.phone !== undefined) setProfile(prev => ({ ...prev, phone: editedData.phone }));
        if (editedData.short_bio !== undefined) setBio(editedData.short_bio);
        if (editedData.subjects !== undefined) setSelectedSubjects(editedData.subjects);
        else if (editedData.subject !== undefined) setSelectedSubjects([editedData.subject]);
        if (editedData.subject_types !== undefined) setSelectedSubjectTypes(editedData.subject_types);
        if (editedData.grade_levels !== undefined) setSelectedGrades(editedData.grade_levels);
        if (editedData.languages !== undefined) setSelectedLanguages(editedData.languages);
        if (editedData.pricing_private_per_hour !== undefined) setPricePerHourArr([parseFloat(editedData.pricing_private_per_hour)]);
        if (editedData.pricing_group_per_hour !== undefined) setGroupPricePerHourArr([parseFloat(editedData.pricing_group_per_hour)]);
        if (editedData.iban !== undefined) setIban(editedData.iban);
        if (editedData.bank_name !== undefined) setBankName(editedData.bank_name);
        if (editedData.account_holder_name !== undefined) setAccountHolderName(editedData.account_holder_name);
        if (editedData.availability_schedule !== undefined) setAvailabilitySchedule(editedData.availability_schedule);
        if (editedData.lesson_format_preference !== undefined) setLessonFormatPreference(editedData.lesson_format_preference);
        if (editedData.intro_video_url !== undefined) setIntroVideoUrl(editedData.intro_video_url || null);

        // Update wizard data for review to reflect changes
        setWizardDataForReview(prev => ({ ...prev, ...editedData }));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  // Load teacher locations from database
  useEffect(() => {
    const loadTeacherLocations = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('teacher_locations')
        .select('*')
        .eq('teacher_uuid', user.id)
        .eq('is_active', true)
        .order('is_default', { ascending: false });

      if (data) {
        setTeacherLocations(data);
      } else if (error) {
        console.error('Error loading teacher locations:', error);
      }
    };

    loadTeacherLocations();
  }, []);

  // Update wizardDataForReview when teacher data changes
  useEffect(() => {
    if (profile.name && profile.email) {
      setWizardDataForReview(prev => ({
        ...prev,
        full_name: profile.name,
        email: profile.email,
        phone: profile.phone,
        profile_photo: imagePreview || profile.image || '',
        photo_url: imagePreview || profile.image || '',
        short_bio: bio || '',
        bio: bio || '',
        subjects: selectedSubjects,
        subject: selectedSubjects.length > 0 ? selectedSubjects[0] : '',
        subject_types: selectedSubjectTypes,
        grade_levels: selectedGrades,
        languages: selectedLanguages.filter(lang => lang && lang.language && lang.level).map(lang => {
          // Ensure language is in proper object format
          if (typeof lang === 'object' && lang !== null) {
            return { language: String(lang.language || ''), level: String(lang.level || '') };
          }
          // If it's a string, try to parse it as JSON
          if (typeof lang === 'string') {
            try {
              const parsed = JSON.parse(lang);
              return { language: String(parsed.language || ''), level: String(parsed.level || '') };
            } catch (e) {
              return null;
            }
          }
          return null;
        }).filter(Boolean),
        intro_video_url: introVideoUrl || '',
        introVideoUrl: introVideoUrl || '',
        hourly_rate: pricePerHourArr && pricePerHourArr.length > 0 ? parseFloat(pricePerHourArr[0]) : null,
        group_hourly_rate: groupPricePerHourArr && groupPricePerHourArr.length > 0 ? parseFloat(groupPricePerHourArr[0]) : null,
        // Format pricing based on lesson_format_preference
        ...(lessonFormatPreference === 'both' ? {
          // For 'both', we need separate online and face-to-face pricing
          // Use the same price for both if only one is set, or use the stored values
          pricing_private_online_per_hour: pricePerHourArr && pricePerHourArr.length > 0 ? `${pricePerHourArr[0]} BD` : '',
          pricing_group_online_per_hour: groupPricePerHourArr && groupPricePerHourArr.length > 0 ? `${groupPricePerHourArr[0]} BD` : '',
          pricing_private_face_to_face_per_hour: pricePerHourArr && pricePerHourArr.length > 0 ? `${pricePerHourArr[0]} BD` : '',
          pricing_group_face_to_face_per_hour: groupPricePerHourArr && groupPricePerHourArr.length > 0 ? `${groupPricePerHourArr[0]} BD` : '',
          group_pricing_model_online: groupPricingModel || 'per_person',
          group_pricing_model_face_to_face: groupPricingModel || 'per_person'
        } : {
          // For 'online_only' or 'face_to_face_only', use simple pricing fields
          pricing_private_per_hour: pricePerHourArr && pricePerHourArr.length > 0 ? `${pricePerHourArr[0]} BD` : '',
          pricing_group_per_hour: groupPricePerHourArr && groupPricePerHourArr.length > 0 ? `${groupPricePerHourArr[0]} BD` : '',
          group_pricing_model: groupPricingModel || 'per_person'
        }),
        qualifications: qualificationDocs,
        certificates: qualificationDocs,
        availability_schedule: availabilitySchedule,
        availabilitySchedule: availabilitySchedule,
        lesson_format_preference: lessonFormatPreference || '',
        experiences: experiences || [],
        experience_none: experienceNone || false,
        teaching_locations: teacherLocations.length > 0 ? teacherLocations.map(loc => ({
          formatted_address: loc.formatted_address || loc.address || '',
          address: loc.formatted_address || loc.address || '',
          latitude: loc.latitude,
          longitude: loc.longitude,
          place_id: loc.place_id,
          is_default: loc.is_default
        })) : []
      }));
    }
  }, [profile.name, profile.email, profile.phone, imagePreview, profile.image, bio, selectedSubjects, selectedSubjectTypes, selectedGrades, selectedLanguages, introVideoUrl, pricePerHourArr, groupPricePerHourArr, groupPricingModel, qualificationDocs, availabilitySchedule, lessonFormatPreference, teacherLocations, experiences, experienceNone]);

  // Fetch statistics
  useEffect(() => {
    async function fetchStats() {
      try {
        setLoadingStats(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const teacherEmail = user.email;
        console.log('[TeacherProfile] Fetching stats for teacher:', teacherEmail);
        const { data: bookings, error: bookingsError } = await getTeacherBookingRequests(teacherEmail);
        const { data: ratingData } = await getTeacherRatingSummary(teacherEmail);

        console.log('[TeacherProfile] Bookings fetch result:', {
          success: !bookingsError,
          bookingsCount: bookings?.length || 0,
          error: bookingsError?.message
        });

        if (bookings) {
          const totalSessions = bookings.filter(b => b.status === 'accepted').length;
          const today = new Date().toISOString().split('T')[0];
          const upcomingSessions = bookings.filter(b => 
            b.status === 'accepted' && b.date >= today
          ).length;
          const completedSessions = bookings.filter(b => 
            b.status === 'accepted' && b.date < today
          ).length;
          const pendingRequests = bookings.filter(b => b.status === 'pending').length;
          const totalEarnings = bookings
            .filter(b => b.status === 'accepted')
            .reduce((sum, b) => sum + (parseFloat(b.total_price) || 0), 0);
          const uniqueStudents = new Set(bookings.map(b => b.student_email)).size;
          
          // Calculate completed bookings for analytics
          const completedBookings = bookings.filter(b => {
            if (b.status !== 'accepted') return false;
            const sessionDate = new Date(b.date + 'T' + (b.time_to || '23:59'));
            return sessionDate < new Date();
          });

          // Fetch REAL payment data from teacher_payouts table and booking_requests
          const payoutResult = await getPayoutHistory(teacherEmail);
          const eligibleResult = await getEligiblePayoutBookings(teacherEmail);

          // Debug logging for payment data
          console.log('[TeacherProfile] Payout history result:', payoutResult);
          console.log('[TeacherProfile] Eligible bookings result:', eligibleResult);
          console.log('[TeacherProfile] Completed bookings count:', completedBookings.length);
          console.log('[TeacherProfile] All bookings count:', bookings.length);
          console.log('[TeacherProfile] Accepted bookings:', bookings.filter(b => b.status === 'accepted').length);
          if (completedBookings.length > 0) {
            console.log('[TeacherProfile] Sample completed booking:', {
              date: completedBookings[0].date,
              status: completedBookings[0].status,
              total_price: completedBookings[0].total_price
            });
          }

          const payoutHistory = payoutResult.payouts || [];
          const eligibleBookings = eligibleResult.bookings || [];

          // Calculate real payment amounts from actual data
          // PAID OUT: Completed payouts from teacher_payouts table
          const paidAmount = (payoutHistory || [])
            .filter(p => p.payout_status === 'completed')
            .reduce((sum, p) => sum + (parseFloat(p.total_amount) || 0), 0);

          // PROCESSING: Payouts that are pending or processing in teacher_payouts table
          const processingAmount = (payoutHistory || [])
            .filter(p => p.payout_status === 'pending' || p.payout_status === 'processing')
            .reduce((sum, p) => sum + (parseFloat(p.total_amount) || 0), 0);

          // PENDING: Sessions that are paid but not yet paid out to teacher
          // Uses teacher_payout_amount (teacher earnings after commission) matching admin calculation
          const pendingAmount = (eligibleBookings || [])
            .reduce((sum, b) => sum + (parseFloat(b.teacher_payout_amount) || parseFloat(b.total_price) || 0), 0);

          // Count sessions for display
          const paidSessions = (payoutHistory || []).filter(p => p.payout_status === 'completed');
          const pendingPayments = (payoutHistory || []).filter(p => p.payout_status === 'pending' || p.payout_status === 'processing');
          const unpaidSessions = eligibleBookings || [];
          
          // Calculate loyalty tier
          let loyaltyTier = 'Bronze';
          let loyaltyBadge = '🥉';
          if (totalSessions >= 50) {
            loyaltyTier = 'Gold';
            loyaltyBadge = '🥇';
          } else if (totalSessions >= 20) {
            loyaltyTier = 'Silver';
            loyaltyBadge = '🥈';
          }
          
          // Calculate monthly earnings for last 6 months
          const monthlyEarnings = {};
          const monthlySessions = {};
          const now = new Date();
          for (let i = 5; i >= 0; i--) {
            const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
            monthlyEarnings[monthKey] = 0;
            monthlySessions[monthKey] = 0;
          }
          
          completedBookings.forEach(booking => {
            try {
              const bookingDate = new Date(booking.date);
              const monthKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}`;
              if (monthlyEarnings.hasOwnProperty(monthKey)) {
                monthlyEarnings[monthKey] += parseFloat(booking.total_price) || 0;
                monthlySessions[monthKey] += 1;
              } else {
                console.log('[TeacherProfile] Booking month not in range:', monthKey, '- booking date:', booking.date);
              }
            } catch (e) {
              // Skip invalid dates
            }
          });

          // Debug: Log final monthly data
          console.log('[TeacherProfile] Monthly earnings:', monthlyEarnings);
          console.log('[TeacherProfile] Monthly sessions:', monthlySessions);
          console.log('[TeacherProfile] Month keys in range:', Object.keys(monthlyEarnings));

          // Calculate student growth (new students per month)
          const monthlyNewStudents = {};
          const studentFirstSession = {};
          bookings.filter(b => b.status === 'accepted').forEach(booking => {
            const studentEmail = booking.student_email;
            if (!studentFirstSession[studentEmail]) {
              try {
                const bookingDate = new Date(booking.date);
                const monthKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}`;
                if (monthlyEarnings.hasOwnProperty(monthKey)) {
                  monthlyNewStudents[monthKey] = (monthlyNewStudents[monthKey] || 0) + 1;
                }
                studentFirstSession[studentEmail] = true;
              } catch (e) {
                // Skip invalid dates
              }
            }
          });

          // Calculate earnings change (compare last 3 months vs previous 3 months)
          const monthKeys = Object.keys(monthlyEarnings);
          const last3Months = monthKeys.slice(-3);
          const prev3Months = monthKeys.slice(0, 3);
          const last3Total = last3Months.reduce((sum, key) => sum + (monthlyEarnings[key] || 0), 0);
          const prev3Total = prev3Months.reduce((sum, key) => sum + (monthlyEarnings[key] || 0), 0);
          const earningsChange = prev3Total > 0
            ? Math.round(((last3Total - prev3Total) / prev3Total) * 100)
            : (last3Total > 0 ? 100 : 0);

          setStats({
            totalStudents: uniqueStudents,
            totalSessions,
            upcomingSessions,
            completedSessions,
            pendingRequests,
            totalEarnings,
            averageRating: ratingData?.avg_rating || 0,
            ratingsCount: ratingData?.ratings_count || 0,
            paidSessions: paidSessions.length,
            pendingPayments: pendingPayments.length,
            unpaidSessions: unpaidSessions.length,
            paidAmount,
            processingAmount,
            pendingAmount,
            loyaltyTier,
            loyaltyBadge,
            monthlyEarnings,
            monthlySessions,
            monthlyNewStudents,
            earningsChange
          });
        }
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoadingStats(false);
      }
    }
    fetchStats();
  }, []);


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    
    // Create image URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setShowCropModal(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropComplete = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }

      // Get cropped image
      const croppedImageUrl = await getCroppedImg(imageToCrop, croppedAreaPixels);
      if (!croppedImageUrl) {
        setError('Failed to crop image.');
        setLoading(false);
        return;
      }

      // Convert blob URL to file
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], `profile-${user.id}-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Upload to Supabase
      const filePath = `user-profiles/${user.id}/${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) {
        setError('Failed to upload image to Supabase Storage.');
        setLoading(false);
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData?.publicUrl;
      
      if (!publicUrl) {
        setError('Failed to get public URL for image.');
        setLoading(false);
        return;
      }

      setImagePreview(publicUrl);
      
      // Update Teachers table
      const { data: updateData, error: updateError } = await supabase
        .from('Teachers')
        .update({ profile_photo: publicUrl })
        .eq('email', user.email)
        .select()
        .single();

      if (updateError) {
        console.error('Profile photo update error:', updateError);
        setError(`Image uploaded, but failed to save URL to profile: ${updateError.message}`);
        setLoading(false);
        return;
      }

      if (updateData) {
        setProfile(prev => ({ ...prev, image: publicUrl }));
        
        // Refresh the cached teacher profile in the layout so it updates everywhere
        if (refreshTeacherProfile && typeof refreshTeacherProfile === 'function') {
          console.log('🔄 Refreshing cached teacher profile after image update...');
          refreshTeacherProfile();
        }
        
        setSuccess('Profile picture updated!');
      }

      // Clean up
      URL.revokeObjectURL(imageToCrop);
      URL.revokeObjectURL(croppedImageUrl);
      setShowCropModal(false);
      setImageToCrop(null);
      setLoading(false);
    } catch (err) {
      console.error('Error cropping/uploading image:', err);
      setError(`Failed to upload image: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handleCropCancel = () => {
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setShowCropModal(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm(isArabic 
      ? 'هل أنت متأكد من حذف صورة الملف الشخصي؟' 
      : 'Are you sure you want to delete your profile picture?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }

      // Update Teachers table to remove photo
      const { data: updateData, error: updateError } = await supabase
        .from('Teachers')
        .update({ profile_photo: null })
        .eq('email', user.email)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to delete profile photo:', updateError);
        setError('Failed to delete profile picture.');
        setLoading(false);
        return;
      }

      // Clear local state
      setProfile(prev => ({ ...prev, image: null }));
      setImagePreview(null);
      
      // Refresh the cached teacher profile in the layout so it updates everywhere
      if (refreshTeacherProfile && typeof refreshTeacherProfile === 'function') {
        console.log('🔄 Refreshing cached teacher profile after image deletion...');
        refreshTeacherProfile();
      }
      
      setSuccess(isArabic ? 'تم حذف صورة الملف الشخصي' : 'Profile picture deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting profile picture:', err);
      setError('Failed to delete profile picture.');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError(isArabic ? 'الرجاء تحميل ملف فيديو صالح' : 'Please upload a valid video file.');
      return;
    }
    
    // Validate file size (max 50MB for safety, but we'll check duration)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError(isArabic ? 'حجم الملف كبير جداً. الحد الأقصى 50 ميجابايت' : 'File size too large. Maximum 50MB.');
      return;
    }
    
    setVideoUploading(true);
    setError('');
    setSuccess('');
    
    try {
      // Create video element to check duration
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = async () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration; // in seconds
        const maxDuration = 120; // 2 minutes = 120 seconds
        
        if (duration > maxDuration) {
          setError(isArabic 
            ? `مدة الفيديو طويلة جداً. الحد الأقصى دقيقتان (${Math.round(duration)} ثانية)` 
            : `Video duration too long. Maximum 2 minutes (${Math.round(duration)} seconds)`);
          setVideoUploading(false);
          return;
        }
        
        // Upload video
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError(isArabic ? 'المستخدم غير مصادق عليه' : 'User not authenticated.');
          setVideoUploading(false);
          return;
        }
        
        const fileExt = file.name.split('.').pop();
        const fileName = `intro-video-${Date.now()}.${fileExt}`;
        const filePath = `teacher-intros/${user.id}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('teacher-intros')
          .upload(filePath, file, { upsert: true });
        
        if (uploadError) {
          setError(isArabic ? 'فشل تحميل الفيديو' : 'Failed to upload video.');
          setVideoUploading(false);
          return;
        }
        
        const { data: publicUrlData } = supabase.storage
          .from('teacher-intros')
          .getPublicUrl(filePath);
        
        const publicUrl = publicUrlData?.publicUrl;
        if (!publicUrl) {
          setError(isArabic ? 'فشل الحصول على رابط الفيديو' : 'Failed to get video URL.');
          setVideoUploading(false);
          return;
        }
        
        setIntroVideoUrl(publicUrl);
        setSuccess(isArabic ? 'تم تحميل الفيديو بنجاح!' : 'Video uploaded successfully!');
        
        // Update database
        const { error: updateError } = await supabase
          .from('Teachers')
          .update({ intro_video_url: publicUrl })
          .eq('email', user.email);
        
        if (updateError) {
          console.error('Failed to update video URL:', updateError);
        }
        
        setVideoUploading(false);
      };
      
      video.onerror = () => {
        setError(isArabic ? 'خطأ في قراءة ملف الفيديو' : 'Error reading video file.');
        setVideoUploading(false);
      };
      
      video.src = URL.createObjectURL(file);
    } catch (err) {
      setError(isArabic ? 'فشل تحميل الفيديو' : 'Failed to upload video.');
      setVideoUploading(false);
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  // Add helper function before handleSubmit
  const getNumberFromBD = (val) => {
    if (typeof val === 'string') {
      return parseFloat(val.replace(' BD', ''));
    }
    if (typeof val === 'number') {
      return val;
    }
    return null;
  };

  // Calculate platform commission based on actual fee structure: 15% + 0.500 BD
  const calculatePlatformFee = (price) => {
    const numPrice = getNumberFromBD(price);
    const PLATFORM_BASE_COMMISSION = 0.500; // Fixed 0.5 BD
    const PLATFORM_COMMISSION_PERCENTAGE = 0.15; // 15%
    return (numPrice * PLATFORM_COMMISSION_PERCENTAGE) + PLATFORM_BASE_COMMISSION;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    // Required fields validation
    // Check required fields: Profile Image, Bio, Teaching Details (Subjects, Grades, Languages), Pricing
    const missingFields = [];
    
    if (!imagePreview && !profile.image) {
      missingFields.push(isArabic ? 'صورة الملف الشخصي' : 'Profile Image');
    }
    if (!bio || !bio.trim()) {
      missingFields.push(isArabic ? 'نبذة' : 'Bio');
    }
    if (selectedSubjects.length === 0) {
      missingFields.push(isArabic ? 'المواد' : 'Subjects');
    }
    // Check if "Other" is selected but no custom subject name provided
    if (selectedSubjects.includes(isArabic ? 'أخرى' : 'Other') && !customSubject.trim()) {
      missingFields.push(isArabic ? 'اسم المادة المخصصة' : 'Custom Subject Name');
    }
    if (selectedGrades.length === 0) {
      missingFields.push(isArabic ? 'المستويات' : 'Grade Levels');
    }
    if (selectedLanguages.length === 0) {
      missingFields.push(isArabic ? 'اللغات' : 'Languages');
    }
    if (pricePerHourArr.length === 0 || !pricePerHourArr[0] || pricePerHourArr[0] <= 0) {
      missingFields.push(isArabic ? 'السعر للساعة' : 'Price Per Hour');
    }
    if (!lessonFormatPreference) {
      missingFields.push(isArabic ? 'تفضيل شكل الدرس' : 'Lesson Format Preference');
    }
    if ((lessonFormatPreference === 'face_to_face_only' || lessonFormatPreference === 'both') && teacherLocations.length === 0) {
      missingFields.push(isArabic ? 'موقع واحد على الأقل' : 'At least one location');
    }

    if (missingFields.length > 0) {
      const errorMsg = isArabic 
        ? `يرجى ملء الحقول المطلوبة التالية: ${missingFields.join(', ')}`
        : `Please fill in the following required fields: ${missingFields.join(', ')}`;
      setError(errorMsg);
      setLoading(false);
      return;
    }
    
    // Validate Availability Schedule - All days must be configured
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = isArabic 
      ? { monday: 'الإثنين', tuesday: 'الثلاثاء', wednesday: 'الأربعاء', thursday: 'الخميس', friday: 'الجمعة', saturday: 'السبت', sunday: 'الأحد' }
      : { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
    
    const unconfiguredDays = [];
    days.forEach(day => {
      const dayData = availabilitySchedule[day] || { allDay: false, notAvailable: false, slots: [] };
      const isConfigured = dayData.allDay || dayData.notAvailable || (dayData.slots && dayData.slots.length > 0);
      if (!isConfigured) {
        unconfiguredDays.push(dayLabels[day]);
      }
    });
    
    if (unconfiguredDays.length > 0) {
      const errorMsg = isArabic 
        ? `يرجى تحديد حالة التوفر لجميع الأيام. الأيام غير المحددة: ${unconfiguredDays.join(', ')}. يمكنك اختيار "متاح طوال اليوم" أو "غير متاح" أو إضافة فترات زمنية محددة.`
        : `Please set availability for all days. Unconfigured days: ${unconfiguredDays.join(', ')}. You can choose "Available All Day", "Not Available", or add specific time slots.`;
      setError(errorMsg);
      setLoading(false);
      return;
    }
    
    // Optional: Check Date of Birth for age validation if provided
    if (dob && !isAtLeast18YearsOld(dob)) {
      setError(isArabic 
        ? 'يجب أن تكون 18 عامًا على الأقل لإنشاء ملف معلم.'
        : 'You must be at least 18 years old to create a teacher profile.');
      setLoading(false);
      return;
    }
    try {
      const email = profile.email;
      const upsertPayload = {
        full_name: profile.name,
        email: profile.email,
        phone: profile.phone && profile.phone.trim() !== '' ? profile.phone : null,
        dob: dob && dob.trim() !== '' ? dob : null, // Convert empty string to null for date field
        qualifications: qualificationDocs, // Save as array of objects
        bio: bio && bio.trim() !== '' ? bio : null,
        subjects: selectedSubjects,
        grade_levels: selectedGrades,
        languages: selectedLanguages.filter(lang => lang.language && lang.level), // Only save languages with both language and level
        // Note: experiences and experience_none are stored in submission_data, not in Teachers table
        hourly_rate: getNumberFromBD(pricePerHourArr[0]),
        group_hourly_rate: getNumberFromBD(groupPricePerHourArr[0]),
        group_pricing_model: groupPricingModel,
        profile_photo: imagePreview || profile.image || null,
        intro_video_url: introVideoUrl && introVideoUrl.trim() !== '' ? introVideoUrl : null,
        password_hash: passwordHash && passwordHash.trim() !== '' ? passwordHash : null,
        availability_schedule: availabilitySchedule,
        lesson_format_preference: lessonFormatPreference,
        // Banking information
        iban: iban && iban.trim() !== '' ? iban.replace(/\s/g, '').toUpperCase() : null,
        bank_name: bankName && bankName.trim() !== '' ? bankName : null,
        account_holder_name: accountHolderName && accountHolderName.trim() !== '' ? accountHolderName : null,
        // Keep preferred_place for backward compatibility (use first location's address)
        preferred_place: teacherLocations.length > 0
          ? teacherLocations.find(loc => loc.is_default)?.formatted_address || teacherLocations[0].formatted_address
          : null,
      };
      console.log('Upsert payload:', upsertPayload);
      const { data, error } = await supabase
        .from('Teachers')
        .upsert(upsertPayload, { onConflict: ['email'], returning: 'representation' });
      if (error) {
        console.error('Supabase upsert error:', error);
        // Provide user-friendly error message
        let errorMessage = 'Failed to update profile.';
        if (error.message) {
          if (error.message.includes('date')) {
            errorMessage = isArabic 
              ? 'خطأ في تاريخ الميلاد. يرجى التأكد من إدخال تاريخ صحيح.'
              : 'Invalid date format. Please ensure the date of birth is entered correctly.';
          } else if (error.message.includes('null value') || error.message.includes('not-null')) {
            errorMessage = isArabic
              ? 'بعض الحقول المطلوبة مفقودة. يرجى ملء جميع الحقول المطلوبة.'
              : 'Some required fields are missing. Please fill in all required fields.';
          } else {
            errorMessage = error.message;
          }
        }
        setError(errorMessage);
      } else {
        setSuccess('Profile updated!');
        setTimeout(() => {
          navigate('/dashboard/teacher/dashboard');
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }, 1000);
      }
    } catch (err) {
      alert('Caught exception: ' + (err.message || JSON.stringify(err)));
      setError('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setProfile({ name: '', email: '', phone: '', school: '', grade: '', image: null, oldPassword: '', newPassword: '', confirmPassword: '', schoolOther: '' });
    setImagePreview(null);
    localStorage.clear();
    navigate('/signin');
  };

  // Location Management Handlers
  const handleAddLocation = async (locationData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // If this is the first location, make it default
    const isFirstLocation = teacherLocations.length === 0;

    const { data, error } = await supabase
      .from('teacher_locations')
      .insert([{
        teacher_uuid: user.id,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        formatted_address: locationData.formatted_address,
        place_name: locationData.place_name || 'Custom Location',
        place_id: locationData.place_id,
        is_default: isFirstLocation,
        is_active: true
      }])
      .select()
      .single();

    if (data) {
      setTeacherLocations([...teacherLocations, data]);
      setShowLocationPicker(false);
      setSuccess(isArabic ? 'تم إضافة الموقع بنجاح' : 'Location added successfully');
    } else if (error) {
      console.error('Error adding location:', error);
      setError(isArabic ? 'فشل في إضافة الموقع' : 'Failed to add location');
    }
  };

  const handleRemoveLocation = async (locationId) => {
    // Prevent removing the last location if face-to-face is required
    if (teacherLocations.length === 1 && (lessonFormatPreference === 'face_to_face_only' || lessonFormatPreference === 'both')) {
      setError(isArabic
        ? 'يجب أن يكون لديك موقع واحد على الأقل للتدريس وجهاً لوجه'
        : 'You must have at least one location for face-to-face teaching');
      return;
    }

    const { error } = await supabase
      .from('teacher_locations')
      .update({ is_active: false })
      .eq('id', locationId);

    if (!error) {
      setTeacherLocations(teacherLocations.filter(loc => loc.id !== locationId));
      setSuccess(isArabic ? 'تم حذف الموقع' : 'Location removed');
    } else {
      console.error('Error removing location:', error);
      setError(isArabic ? 'فشل في حذف الموقع' : 'Failed to remove location');
    }
  };

  const handleSetDefaultLocation = async (locationId) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // First, remove default from all locations
    await supabase
      .from('teacher_locations')
      .update({ is_default: false })
      .eq('teacher_uuid', user.id);

    // Then set the new default
    const { error } = await supabase
      .from('teacher_locations')
      .update({ is_default: true })
      .eq('id', locationId);

    if (!error) {
      setTeacherLocations(teacherLocations.map(loc => ({
        ...loc,
        is_default: loc.id === locationId
      })));
      setSuccess(isArabic ? 'تم تعيين الموقع الافتراضي' : 'Default location set');
    } else {
      console.error('Error setting default location:', error);
      setError(isArabic ? 'فشل في تعيين الموقع الافتراضي' : 'Failed to set default location');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    if (!profile.oldPassword || !profile.newPassword || !profile.confirmPassword) {
      setError('Please fill in all password fields.');
      setLoading(false);
      return;
    }
    if (profile.newPassword !== profile.confirmPassword) {
      setError('New passwords do not match.');
      setLoading(false);
      return;
    }
    try {
      const email = profile.email;
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: profile.oldPassword,
      });
      if (signInError || !data.user) {
        setError('Old password is incorrect.');
        setLoading(false);
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({
        password: profile.newPassword,
      });
      if (updateError) {
        setError('Failed to update password.');
      } else {
        setSuccess('Password updated successfully!');
        setProfile(prev => ({ ...prev, oldPassword: '', newPassword: '', confirmPassword: '' }));
      }
    } catch (err) {
      setError('Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  // Add back the handleBioChange function before handleQualificationDocs
  const handleBioChange = (e) => {
    const words = e.target.value.split(/\s+/).filter(Boolean);
    if (words.length <= 23) {
      setBio(e.target.value);
      setBioError('');
    } else {
      setBioError('Bio must be 23 words or less.');
    }
  };

  // Availability Schedule Helper Functions
  const toggleAllDay = (day) => {
    setAvailabilitySchedule(prev => ({
      ...prev,
      [day]: {
        allDay: !prev[day]?.allDay,
        notAvailable: false, // Disable notAvailable when enabling allDay
        slots: prev[day]?.allDay ? prev[day].slots : []
      }
    }));
  };

  const toggleNotAvailable = (day) => {
    setAvailabilitySchedule(prev => ({
      ...prev,
      [day]: {
        allDay: false, // Disable allDay when marking as not available
        notAvailable: !prev[day]?.notAvailable,
        slots: prev[day]?.notAvailable ? prev[day].slots : []
      }
    }));
  };

  const addTimeSlot = (day) => {
    setAvailabilitySchedule(prev => ({
      ...prev,
      [day]: {
        allDay: false, // Disable allDay when adding specific slots
        notAvailable: false, // Disable notAvailable when adding slots
        slots: [...(prev[day]?.slots || []), { from: '09:00', to: '12:00' }]
      }
    }));
  };

  const removeTimeSlot = (day, index) => {
    setAvailabilitySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: (prev[day]?.slots || []).filter((_, i) => i !== index)
      }
    }));
  };

  const updateTimeSlot = (day, index, field, value) => {
    setAvailabilitySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        allDay: false, // Disable allDay when editing specific slots
        notAvailable: false, // Disable notAvailable when editing slots
        slots: (prev[day]?.slots || []).map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  // Toggle custom times section for a day
  const toggleCustomTimes = (day) => {
    setShowCustomTimes(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
    // If opening custom times, add a default slot if none exist
    if (!showCustomTimes[day]) {
      const dayData = availabilitySchedule[day] || { allDay: false, notAvailable: false, slots: [] };
      if (dayData.slots.length === 0) {
        addTimeSlot(day);
      }
    }
  };

  // Copy schedule from one day to others
  const copyScheduleToDays = (sourceDay, targetDays) => {
    const sourceData = availabilitySchedule[sourceDay] || { allDay: false, notAvailable: false, slots: [] };
    setAvailabilitySchedule(prev => {
      const updated = { ...prev };
      targetDays.forEach(targetDay => {
        updated[targetDay] = {
          allDay: sourceData.allDay,
          notAvailable: sourceData.notAvailable,
          slots: sourceData.slots.map(slot => ({ ...slot })) // Deep copy slots
        };
      });
      return updated;
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleQualificationDocs = async (e) => {
    const files = Array.from(e.target.files);
    const urls = [];
    setQualificationDocsError('');
    setQualificationDocsSuccess('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setQualificationDocsError('User not authenticated.');
        return;
      }
      for (const file of files) {
        if (!file.type.match(/pdf|image\//)) {
          setQualificationDocsError('Only PDF or image files are allowed.');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setQualificationDocsError('Each file must be less than 5MB.');
          return;
        }
        const filePath = `user-${user.id}/${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('qualification-documents')
          .upload(filePath, file, { upsert: true });
        if (uploadError) {
          setQualificationDocsError('Failed to upload file.');
          return;
        }
        const { data: publicUrlData } = supabase.storage
          .from('qualification-documents')
          .getPublicUrl(filePath);
        if (publicUrlData && publicUrlData.publicUrl) {
          // Remove trailing slash if present
          let url = publicUrlData.publicUrl.replace(/\/$/, '');
          urls.push({ url, name: '' });
        }
      }
      setQualificationDocs(prev => [...prev, ...urls]);
      setQualificationDocsSuccess('Files uploaded successfully!');
    } catch (err) {
      setQualificationDocsError('Failed to upload files.');
    }
  };

  const handleQualDrop = (e) => {
    e.preventDefault();
    setQualDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setQualificationDocs((prev) => [...prev, ...Array.from(e.dataTransfer.files).map(f => ({ url: f.url, name: '' }))]);
    }
  };

  const handleQualDragOver = (e) => {
    e.preventDefault();
    setQualDragActive(true);
  };

  const handleQualDragLeave = (e) => {
    e.preventDefault();
    setQualDragActive(false);
  };

  const handleRemoveQualificationDoc = (index) => {
    setQualificationDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQualificationNameChange = (index, event) => {
    const newQualificationDocs = [...qualificationDocs];
    newQualificationDocs[index].name = event.target.value;
    setQualificationDocs(newQualificationDocs);
  };

  if (loading && !profile.name) {
    return (
      <main style={{ flex: 1, padding: '40px 48px', minHeight: '100vh', background: '#f7f9fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <FaHourglassHalf size={48} style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: '#6b7280' }}>Loading profile...</div>
        </div>
      </main>
    );
  }

  const requiredFields = [
    profile.name,
    profile.email,
    profile.phone,
    dob,
    bio,
    selectedSubjects.length > 0,
    selectedGrades.length > 0,
    selectedLanguages.length > 0,
    pricePerHourArr.length > 0,
    qualificationDocs.length > 0,
  ];
  const completeness = Math.round((requiredFields.filter(Boolean).length / requiredFields.length) * 100);
  
  // Helper function to check if a value is valid (not empty, null, undefined, or "Not provided")
  const isValidField = (value) => {
    if (!value) return false;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 && 
             trimmed !== 'Not provided' && 
             trimmed !== 'غير متوفر' &&
             trimmed.toLowerCase() !== 'null' &&
             trimmed.toLowerCase() !== 'undefined';
    }
    return true;
  };
  
  // Helper function to check if email is valid
  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const trimmed = email.trim();
    return trimmed.length > 0 && 
           trimmed !== 'Not provided' && 
           trimmed !== 'غير متوفر' &&
           trimmed.includes('@') && 
           trimmed.includes('.');
  };
  
  // Profile Checklist - Structured with grouped sections
  // Helper to check if name has both first AND last name
  const hasFullName = () => {
    if (!isValidField(profile.name)) return false;
    const nameParts = profile.name.trim().split(/\s+/);
    return nameParts.length >= 2 && nameParts.every(part => part.length > 0);
  };

  // Section 1: Personal Information
  const personalInfoItems = [
    {
      label: isArabic ? 'الاسم الأول والأخير' : 'First & Last Name',
      complete: hasFullName(),
      required: true
    },
    {
      label: isArabic ? 'البريد الإلكتروني' : 'Email',
      complete: isValidEmail(profile.email),
      required: true
    },
    {
      label: isArabic ? 'رقم الهاتف' : 'Phone',
      complete: isValidField(profile.phone),
      required: true
    },
    {
      label: isArabic ? 'تاريخ الميلاد' : 'Date of Birth',
      complete: isValidField(dob),
      required: true
    }
  ];

  // Section 2: Teaching Details
  const teachingDetailsItems = [
    {
      label: isArabic ? 'أضف سيرتك الذاتية التعليمية' : 'Add your teaching bio',
      complete: isValidField(bio),
      required: true
    },
    {
      label: isArabic ? 'اختر المواد التي تقوم بتدريسها' : 'Choose subjects you teach',
      complete: Array.isArray(selectedSubjects) && 
                selectedSubjects.length > 0 && 
                selectedSubjects.some(s => s && isValidField(s)),
      required: true
    },
    {
      label: isArabic ? 'اختر المستويات الدراسية' : 'Choose grade levels',
      complete: Array.isArray(selectedGrades) && 
                selectedGrades.length > 0 &&
                selectedGrades.some(g => g && isValidField(g)),
      required: true
    },
    {
      label: isArabic ? 'اختر اللغات التي تتحدثها' : 'Choose languages you speak',
      complete: Array.isArray(selectedLanguages) && 
                selectedLanguages.length > 0 &&
                selectedLanguages.some(l => l && isValidField(l)),
      required: true
    }
  ];

  // Section 3: Pricing
  const hasValidPricing = (Array.isArray(pricePerHourArr) && 
                           pricePerHourArr.length > 0 && 
                           pricePerHourArr[0] != null && 
                           pricePerHourArr[0] !== '' &&
                           !isNaN(parseFloat(pricePerHourArr[0])) &&
                           parseFloat(pricePerHourArr[0]) > 0) ||
                          (pricePerHour && 
                           pricePerHour.trim().length > 0 && 
                           !isNaN(parseFloat(pricePerHour)) && 
                           parseFloat(pricePerHour) > 0);

  const pricingItems = [
    {
      label: isArabic ? 'حدد سعرك للساعة' : 'Set your hourly rate',
      complete: hasValidPricing,
      required: true
    }
  ];

  // Section 4: Qualifications (Optional)
  const qualificationsItems = [
    {
      label: isArabic ? 'أضف مؤهلاتك (اختياري)' : 'Add your qualifications (optional)',
      complete: Array.isArray(qualificationDocs) && qualificationDocs.length > 0,
      required: false // Optional
    }
  ];

  // Combine all items into a single list (including optional)
  const allChecklistItems = [
    ...personalInfoItems,
    ...teachingDetailsItems,
    ...pricingItems,
    ...qualificationsItems
  ];

  // Sort checklist: completed items first, incomplete below
  const sortedChecklistItems = [...allChecklistItems].sort((a, b) => {
    if (a.complete && !b.complete) return -1;
    if (!a.complete && b.complete) return 1;
    return 0;
  });

  // Combine all required items for completion check
  const allRequiredItems = [
    ...personalInfoItems,
    ...teachingDetailsItems,
    ...pricingItems
  ];

  // Calculate completion - only count required items
  const requiredItemsComplete = allRequiredItems.filter(item => item.complete).length;
  const totalRequiredItems = allRequiredItems.length;
  const checklistCompleteness = Math.round((requiredItemsComplete / totalRequiredItems) * 100);
  
  // All required items must be complete (optional items don't block)
  const allChecklistComplete = allRequiredItems.every(item => item.complete);

  const tabs = [
    { id: 'overview', label: isArabic ? 'نظرة عامة' : 'Overview', icon: FaChartBar },
    { id: 'profile', label: isArabic ? 'الملف الشخصي' : 'Profile', icon: FaUser },
    { id: 'settings', label: isArabic ? 'الإعدادات' : 'Settings', icon: FaCog }
  ];

  return (
    <main
        style={{
        flex: 1,
        paddingTop: isMobile ? '16px' : '40px',
        paddingRight: isMobile ? '16px' : (sidebarVisible ? '48px' : '48px'),
        paddingBottom: isMobile ? '16px' : '40px',
        paddingLeft: isMobile ? '16px' : (sidebarVisible ? '72px' : '48px'),
          minHeight: '100vh',
        background: '#f7f9fb',
        fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
        transition: 'padding-left 0.3s, padding-right 0.3s',
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <PageHeader 
        title={t.profile} 
        subtitle={isArabic ? 'إدارة ملفك الشخصي وإعدادات التدريس' : 'Manage your profile and teaching settings'} 
        isArabic={isArabic} 
      />

      {/* Alert Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '14px 20px',
          borderRadius: 12,
          marginBottom: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid #fca5a5'
        }}>
          <FaExclamationTriangle size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#166534',
          padding: '14px 20px',
          borderRadius: 12,
          marginBottom: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid #86efac'
        }}>
          <FaCheckCircle size={16} style={{ color: '#16a34a' }} />
          <span>{success}</span>
        </div>
      )}

      {/* Profile Card */}
      <div style={{
        background: '#fff',
        borderRadius: 24,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        marginBottom: 24
      }}>
        {/* Profile Header */}
        <div style={{
          background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
          padding: isMobile ? '20px 16px' : '40px 32px',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 16 : 32,
            flexWrap: 'wrap',
          justifyContent: 'center',
            maxWidth: 1200,
            margin: '0 auto'
          }}>
            {/* Profile Photo */}
            <div style={{ 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={imagePreview || (profile.image && profile.image.trim() !== '' ? profile.image : 'https://www.gravatar.com/avatar/?d=mp&f=y')}
                alt="Profile"
                style={{
                  width: isMobile ? 100 : 140,
                  height: isMobile ? 100 : 140,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: isMobile ? '3px solid #fff' : '4px solid #fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                }}
              />
              {/* Upload Button - Bottom Right */}
              <label
                htmlFor="profile-upload"
                style={{
                  position: 'absolute',
                  bottom: isMobile ? 0 : 2,
                  right: isMobile ? 0 : 2,
                  background: '#FF7426',
                  borderRadius: '50%',
                  border: isMobile ? '2px solid #fff' : '3px solid #fff',
                  padding: 0,
                  cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? 36 : 44,
                  height: isMobile ? 36 : 44,
                  transition: 'all 0.2s ease',
                  zIndex: 10,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                  e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                  e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.4)';
                  }
                }}
                onTouchStart={(e) => {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FaUpload style={{ color: '#fff', fontSize: isMobile ? 14 : 18 }} />
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                  ref={fileInputRef}
                />
              </label>
              {/* Remove Button - Top Left */}
              {(imagePreview || profile.image) && (
                <button
                  type="button"
                  title={isMobile ? (isArabic ? 'حذف الصورة' : 'Remove picture') : (isArabic ? 'حذف صورة الملف الشخصي' : 'Remove profile picture')}
                  onClick={handleDeleteProfilePicture}
                  disabled={loading}
                  style={{
                    position: 'absolute',
                    top: isMobile ? 0 : 2,
                    left: isMobile ? 0 : 2,
                    background: '#dc2626',
                    border: isMobile ? '2px solid #fff' : '3px solid #fff',
                    borderRadius: '50%',
                    width: isMobile ? 36 : 40,
                    height: isMobile ? 36 : 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: isMobile ? 16 : 22,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.4)',
                    transition: 'all 0.2s ease',
                    zIndex: 10,
                    padding: 0,
                    lineHeight: 1,
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    opacity: loading ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile && !loading) {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.background = '#b91c1c';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.background = '#dc2626';
                      e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.4)';
                    }
                  }}
                  onTouchStart={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'scale(0.95)';
                    }
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ×
                </button>
              )}
          </div>

            {/* Profile Info */}
            <div style={{ flex: 1, color: '#fff', textAlign: isMobile ? 'center' : 'left' }}>
              <div style={{ fontSize: isMobile ? 22 : 32, fontWeight: 800, marginBottom: isMobile ? 4 : 8 }}>
                {profile.name || 'Teacher'}
        </div>
              <div style={{ fontSize: isMobile ? 13 : 16, opacity: 0.9, marginBottom: isMobile ? 2 : 4 }}>
                {profile.email}
              </div>
              {selectedSubjects.length > 0 && (
                <div style={{ fontSize: isMobile ? 12 : 14, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start', gap: 6, flexWrap: 'wrap', marginTop: isMobile ? 4 : 4 }}>
                  <FaBook size={isMobile ? 12 : 14} />
                  <span>{selectedSubjects.map(s => s && s.startsWith('Custom: ') ? s.replace('Custom: ', '') : s).join(', ')}</span>
                </div>
              )}
            </div>

            {/* Profile Completeness Badge */}
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              borderRadius: isMobile ? 12 : 16,
              padding: isMobile ? '12px 16px' : '20px 24px',
              textAlign: 'center',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '180px' : 'none',
              minWidth: isMobile ? 'auto' : 140
            }}>
              <div style={{ fontSize: isMobile ? 24 : 36, fontWeight: 900, marginBottom: isMobile ? 2 : 4, color: '#fff' }}>
                {completeness}%
              </div>
              <div style={{ fontSize: isMobile ? 10 : 12, color: '#fff' }}>
                {t.completeness}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: isMobile ? 4 : 8,
          padding: isMobile ? '0 16px' : '0 32px',
          borderBottom: '2px solid #e5e7eb',
          background: '#fff',
          overflowX: 'auto'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
          style={{
                padding: isMobile ? '12px 16px' : '16px 24px',
                borderRadius: '12px 12px 0 0',
                fontWeight: 700,
                fontSize: isMobile ? 13 : 15,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: 'none',
                background: activeTab === tab.id ? '#FF7426' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 4 : 8,
                marginBottom: -2,
                borderBottom: activeTab === tab.id ? '3px solid #FF7426' : '3px solid transparent',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={e => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                }
              }}
              onMouseOut={e => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
            >
              <tab.icon size={isMobile ? 14 : 16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: isMobile ? '16px' : '32px' }}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              {/* Top Row: 6 Statistics Cards - White cards with icons matching images */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: isMobile ? 12 : 16,
                marginBottom: isMobile ? 20 : 24
              }}>
                {/* Total Students Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: isMobile ? 10 : 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {isMobile ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="9" cy="7" r="4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {isArabic ? 'إجمالي الطلاب' : 'TOTAL STUDENTS'}
                        </span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                        {loadingStats ? '...' : stats.totalStudents}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="9" cy="7" r="4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#10b981' }}>+12%</span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                        {loadingStats ? '...' : stats.totalStudents}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                        {isArabic ? 'إجمالي الطلاب' : 'Total Students'}
                      </div>
                    </>
                  )}
                </div>

                {/* Upcoming Sessions Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: isMobile ? 10 : 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {isMobile ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {isArabic ? 'الجلسات القادمة' : 'UPCOMING SESSIONS'}
                        </span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                        {loadingStats ? '...' : stats.upcomingSessions}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', background: '#fef3c7', padding: '2px 8px', borderRadius: 12 }}>{isArabic ? 'قريباً' : 'Soon'}</span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                        {loadingStats ? '...' : stats.upcomingSessions}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                        {isArabic ? 'الجلسات القادمة' : 'Upcoming Sessions'}
                      </div>
                    </>
                  )}
                </div>

                {/* Completed Sessions Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: isMobile ? 10 : 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {isMobile ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="22 4 12 14.01 9 11.01" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {isArabic ? 'الجلسات المكتملة' : 'COMPLETED SESSIONS'}
                        </span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                        {loadingStats ? '...' : stats.completedSessions}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="22 4 12 14.01 9 11.01" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                        {loadingStats ? '...' : stats.completedSessions}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                        {isArabic ? 'الجلسات المكتملة' : 'Completed Sessions'}
                      </div>
                    </>
                  )}
                </div>

                {/* Total Earnings Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: isMobile ? 10 : 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {isMobile ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <line x1="12" y1="1" x2="12" y2="23" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {isArabic ? 'إجمالي الأرباح' : 'TOTAL EARNINGS'}
                        </span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                        {loadingStats ? '...' : `${stats.totalEarnings.toFixed(0)} BD`}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <line x1="12" y1="1" x2="12" y2="23" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#10b981' }}>+8%</span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                        {loadingStats ? '...' : `${stats.totalEarnings.toFixed(0)} BD`}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                        {isArabic ? 'إجمالي الأرباح' : 'Total Earnings'}
                      </div>
                    </>
                  )}
                </div>

                {/* Average Rating Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: isMobile ? 10 : 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {isMobile ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {isArabic ? 'متوسط التقييم' : 'AVERAGE RATING'}
                        </span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                        {loadingStats ? '...' : stats.averageRating > 0 ? `${stats.averageRating.toFixed(1)} / 5.0` : 'N/A'}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                        {loadingStats ? '...' : stats.averageRating > 0 ? `${stats.averageRating.toFixed(1)} / 5.0` : 'N/A'}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                        {isArabic ? 'متوسط التقييم' : 'Average Rating'}
                      </div>
                    </>
                  )}
              </div>

                {/* Pending Requests Card */}
                <div style={{
                  background: '#fff',
                  borderRadius: isMobile ? 10 : 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  position: 'relative'
                }}>
                  {isMobile ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="16" y1="13" x2="8" y2="13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="16" y1="17" x2="8" y2="17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="10 9 9 9 8 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {isArabic ? 'الطلبات المعلقة' : 'PENDING REQUESTS'}
                        </span>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                        {loadingStats ? '...' : stats.pendingRequests}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="16" y1="13" x2="8" y2="13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="16" y1="17" x2="8" y2="17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="10 9 9 9 8 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ec4899' }}></div>
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                        {loadingStats ? '...' : stats.pendingRequests}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                        {isArabic ? 'الطلبات المعلقة' : 'Pending Requests'}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Middle Row: Payment Status (Left) and Profile Checklist (Right) */}
                  <div style={{
                    display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 16 : 20,
                marginBottom: isMobile ? 20 : 24
                  }}>
                {/* Payment Status Section - Rebuilt to match image exactly */}
                    <div style={{
                  background: '#fff',
                      borderRadius: 12,
                  padding: isMobile ? 16 : 20,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
                        {isArabic ? 'حالة المدفوعات' : 'Payment Status'}
                      </h3>
                    </div>
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); navigate('/dashboard/teacher/payouts'); }} 
                      style={{ 
                        fontSize: 13, 
                        color: '#FF7426', 
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      {isArabic ? 'سجل المعاملات ←' : 'Transaction History →'}
                    </a>
                  </div>
                  
                  {/* Three horizontal status blocks */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: isMobile ? 8 : 12
                  }}>
                    {/* Paid Out Card */}
                    <div style={{
                      background: '#d1fae5',
                      borderRadius: isMobile ? 8 : 10,
                      padding: isMobile ? 10 : 16,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Banknote background graphic - bottom right */}
                      {!isMobile && (
                      <div style={{
                        position: 'absolute',
                        bottom: -15,
                        right: -15,
                        opacity: 0.15,
                        width: 80,
                        height: 80
                      }}>
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="1.5">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                          <line x1="1" y1="10" x2="23" y2="10"/>
                          <line x1="8" y1="4" x2="8" y2="24"/>
                          <line x1="16" y1="4" x2="16" y2="24"/>
                        </svg>
                      </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 4 : 8 }}>
                        <span style={{
                          fontSize: isMobile ? 9 : 11,
                          fontWeight: 700,
                          color: '#065f46',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {isArabic ? 'تم الدفع' : 'PAID OUT'}
                        </span>
                        <div style={{
                          width: isMobile ? 20 : 28,
                          height: isMobile ? 20 : 28,
                          borderRadius: '50%',
                          background: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <svg width={isMobile ? "10" : "14"} height={isMobile ? "10" : "14"} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{
                        fontSize: isMobile ? 16 : 24,
                        fontWeight: 700,
                        color: '#065f46',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        {loadingStats ? '...' : `${(stats.paidAmount || 0).toFixed(1)} BD`}
                      </div>
                    </div>

                    {/* Processing Card */}
                    <div style={{
                      background: '#dbeafe',
                      borderRadius: isMobile ? 8 : 10,
                      padding: isMobile ? 10 : 16,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 4 : 12 }}>
                        <span style={{
                          fontSize: isMobile ? 9 : 11,
                          fontWeight: 700,
                          color: '#1e40af',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {isArabic ? 'قيد المعالجة' : 'PROCESSING'}
                        </span>
                        <div style={{
                          width: isMobile ? 20 : 28,
                          height: isMobile ? 20 : 28,
                          borderRadius: '50%',
                          background: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <svg width={isMobile ? "10" : "14"} height={isMobile ? "10" : "14"} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"/>
                            <polyline points="1 20 1 14 7 14"/>
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{
                        fontSize: isMobile ? 16 : 24,
                        fontWeight: 700,
                        color: '#1e40af',
                        marginBottom: isMobile ? 6 : 12
                      }}>
                        {loadingStats ? '...' : `${(stats.processingAmount || 0).toFixed(1)} BD`}
                      </div>
                      {/* Progress bar */}
                      <div style={{
                        width: '100%',
                        height: isMobile ? 4 : 6,
                        background: '#bfdbfe',
                        borderRadius: 3,
                        overflow: 'hidden',
                        marginBottom: isMobile ? 4 : 8
                      }}>
                        <div style={{
                          width: '60%',
                          height: '100%',
                          background: '#3b82f6',
                          borderRadius: 3,
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <div style={{
                        fontSize: isMobile ? 9 : 12,
                        color: '#1e40af',
                        fontWeight: 500,
                        lineHeight: 1.4
                      }}>
                        {isArabic ? 'قيد المراجعة من الإدارة' : (isMobile ? 'Being processed' : 'Being processed by admin')}
                      </div>
                    </div>

                    {/* Pending Card */}
                    <div style={{
                      background: '#fef3c7',
                      borderRadius: isMobile ? 8 : 10,
                      padding: isMobile ? 10 : 16,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 4 : 12 }}>
                        <span style={{
                          fontSize: isMobile ? 9 : 11,
                          fontWeight: 700,
                          color: '#92400e',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {isArabic ? 'في الانتظار' : 'PENDING'}
                        </span>
                        <div style={{
                          width: isMobile ? 20 : 28,
                          height: isMobile ? 20 : 28,
                          borderRadius: '50%',
                          background: '#f59e0b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <svg width={isMobile ? "10" : "14"} height={isMobile ? "10" : "14"} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 2h8M8 22h8M12 2v5M12 17v5"/>
                            <path d="M8 7l4 4-4 4M16 7l-4 4 4 4"/>
                            <line x1="12" y1="11" x2="12" y2="13"/>
                          </svg>
                        </div>
                      </div>
                      <div style={{
                        fontSize: isMobile ? 16 : 24,
                        fontWeight: 700,
                        color: '#92400e',
                        marginBottom: isMobile ? 4 : 8
                      }}>
                        {loadingStats ? '...' : `${(stats.pendingAmount || 0).toFixed(1)} BD`}
                      </div>
                      <div style={{
                        fontSize: isMobile ? 9 : 12,
                        color: '#92400e',
                        fontWeight: 500,
                        lineHeight: 1.4
                      }}>
                        {isArabic ? 'متاح للصرف' : (isMobile ? 'Ready for payout' : 'Ready for payout request')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Checklist Section - Matching student style */}
                          <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  height: 'fit-content',
                  width: '100%',
                  alignSelf: 'flex-start'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    marginBottom: isMobile ? 16 : 20,
                    flexWrap: 'wrap',
                    gap: isMobile ? 8 : 0
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? 16 : 18, 
                      fontWeight: 700, 
                      color: '#111827' 
                    }}>
                      {isArabic ? 'قائمة الملف الشخصي' : 'Profile Checklist'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FaCheck size={isMobile ? 16 : 18} style={{ color: '#10b981' }} />
                      <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#10b981' }}>
                        {checklistCompleteness}%
                      </span>
                    </div>
                  </div>
                  <div style={{
                    width: '100%',
                    height: isMobile ? 6 : 8,
                    background: '#e5e7eb',
                    borderRadius: 999,
                    overflow: 'hidden',
                    marginBottom: isMobile ? 16 : 20
                  }}>
                    <div style={{
                      width: `${checklistCompleteness}%`,
                      height: '100%',
                      background: '#10b981',
                      borderRadius: 999,
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {sortedChecklistItems.map((item, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: '#10b981', fontSize: 16 }}>
                          {item.complete ? <FaCheck size={14} style={{ color: '#10b981' }} /> : ''}
                        </span>
                        <span style={{
                          fontSize: 14,
                          color: item.complete ? '#111827' : '#9ca3af',
                          fontWeight: item.complete ? 500 : 400
                        }}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Row: Performance Analytics (Left) and Rating Distribution (Right) */}
                <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 16 : 20,
                marginBottom: isMobile ? 20 : 24
              }}>
                {/* Performance Analytics Section */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 20,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2">
                        <line x1="18" y1="20" x2="18" y2="10"/>
                        <line x1="12" y1="20" x2="12" y2="4"/>
                        <line x1="6" y1="20" x2="6" y2="14"/>
                      </svg>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
                        {isArabic ? 'تحليلات الأداء' : 'Performance Analytics'}
                      </h3>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => setPerformanceTab('earnings')}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: 'none',
                          background: performanceTab === 'earnings' ? '#3b82f6' : 'transparent',
                          color: performanceTab === 'earnings' ? '#fff' : '#6b7280',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {isArabic ? 'الأرباح' : 'Earnings'}
                      </button>
                      <button
                        onClick={() => setPerformanceTab('sessions')}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: 'none',
                          background: performanceTab === 'sessions' ? '#3b82f6' : 'transparent',
                          color: performanceTab === 'sessions' ? '#fff' : '#6b7280',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {isArabic ? 'الجلسات' : 'Sessions'}
                      </button>
                    </div>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr',
                    gap: isMobile ? 16 : 20,
                    alignItems: 'flex-start'
                  }}>
                    {/* Chart Area */}
                    <div style={{
                      minHeight: isMobile ? 200 : 250,
                      background: '#f9fafb',
                      borderRadius: 8,
                      padding: 16,
                      position: 'relative'
                    }}>
                      {performanceTab === 'earnings' && Object.keys(stats.monthlyEarnings || {}).length > 0 ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          height: '100%',
                          gap: 8,
                          paddingBottom: 24
                        }}>
                          {Object.entries(stats.monthlyEarnings).map(([month, earnings], index) => {
                            const maxEarnings = Math.max(...Object.values(stats.monthlyEarnings), 1);
                            const heightPercent = (earnings / maxEarnings) * 100 || 5;
                            const monthNames = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
                            const monthName = monthNames[index] || 'Jan';
                            const isLastMonth = index === Object.keys(stats.monthlyEarnings).length - 1;
                      
                      return (
                              <div key={month} style={{ 
                                flex: 1, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                gap: 8,
                                height: '100%'
                              }}>
                          <div style={{
                            width: '100%',
                                  height: `${Math.max(heightPercent, 5)}%`,
                                  minHeight: '20px',
                                  background: earnings > 0 ? '#3b82f6' : '#e5e7eb',
                                  borderRadius: '4px 4px 0 0',
                            position: 'relative',
                            transition: 'all 0.3s',
                            cursor: 'pointer'
                          }}
                                onMouseEnter={e => {
                                  if (earnings > 0) {
                                    e.currentTarget.style.background = '#2563eb';
                            e.currentTarget.style.transform = 'scaleY(1.05)';
                                  }
                          }}
                                onMouseLeave={e => {
                                  if (earnings > 0) {
                                    e.currentTarget.style.background = '#3b82f6';
                            e.currentTarget.style.transform = 'scaleY(1)';
                                  }
                          }}
                                ></div>
                              <div style={{
                                  fontSize: 12, 
                                  fontWeight: isLastMonth ? 700 : 500, 
                                  color: '#6b7280', 
                                  textAlign: 'center' 
                                }}>
                            {monthName}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                      ) : performanceTab === 'sessions' && Object.keys(stats.monthlySessions || {}).length > 0 ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          height: '100%',
                          gap: 8,
                          paddingBottom: 24
                        }}>
                          {Object.entries(stats.monthlySessions).map(([month, sessions], index) => {
                            const maxSessions = Math.max(...Object.values(stats.monthlySessions), 1);
                            const heightPercent = (sessions / maxSessions) * 100 || 5;
                            const monthNames = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
                            const monthName = monthNames[index] || 'Jan';
                            const isLastMonth = index === Object.keys(stats.monthlySessions).length - 1;
                      
                      return (
                              <div key={month} style={{ 
                                flex: 1, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                gap: 8,
                                height: '100%'
                              }}>
                          <div style={{
                            width: '100%',
                                  height: `${Math.max(heightPercent, 5)}%`,
                                  minHeight: '20px',
                                  background: sessions > 0 ? '#10b981' : '#e5e7eb',
                                  borderRadius: '4px 4px 0 0',
                            position: 'relative',
                            transition: 'all 0.3s',
                            cursor: 'pointer'
                          }}
                                onMouseEnter={e => {
                                  if (sessions > 0) {
                                    e.currentTarget.style.background = '#059669';
                            e.currentTarget.style.transform = 'scaleY(1.05)';
                                  }
                          }}
                                onMouseLeave={e => {
                                  if (sessions > 0) {
                                    e.currentTarget.style.background = '#10b981';
                            e.currentTarget.style.transform = 'scaleY(1)';
                                  }
                          }}
                                ></div>
                              <div style={{
                                  fontSize: 12, 
                                  fontWeight: isLastMonth ? 700 : 500, 
                                  color: '#6b7280', 
                                  textAlign: 'center' 
                                }}>
                            {monthName}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%',
                          color: '#9ca3af',
                          fontSize: 14
                        }}>
                          {isArabic ? 'لا توجد بيانات متاحة' : 'No data available'}
                </div>
              )}
                    </div>

                    {/* Metrics Cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Total Earnings (6m) */}
              <div style={{
                        background: '#f9fafb',
                        borderRadius: 8,
                        padding: 16,
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 500 }}>
                          {isArabic ? 'إجمالي الأرباح (6م)' : 'Total Earnings (6m)'}
                  </div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                          {loadingStats ? '...' : Object.values(stats.monthlyEarnings || {}).reduce((a, b) => a + b, 0).toFixed(0)} BD
                  </div>
                        <div style={{ fontSize: 12, color: stats.earningsChange >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                          {stats.earningsChange >= 0 ? '+' : ''}{stats.earningsChange}% {isArabic ? 'مقارنة بالفترة السابقة' : 'vs last period'}
                </div>
                      </div>
                      {/* Avg. Session Price */}
                <div style={{
                        background: '#f9fafb',
                        borderRadius: 8,
                        padding: 16,
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 500 }}>
                          {isArabic ? 'متوسط سعر الجلسة' : 'Avg. Session Price'}
                </div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
                          {loadingStats ? '...' : stats.completedSessions > 0
                            ? `${(stats.totalEarnings / stats.completedSessions).toFixed(1)} BD`
                            : pricePerHourArr?.[0] ? `${parseFloat(pricePerHourArr[0]).toFixed(1)} BD` : '0.0 BD'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rating Distribution Section - Matching checklist style */}
                      <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  height: 'fit-content',
                  width: '100%'
                }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px 0' }}>
                    {isArabic ? 'توزيع التقييمات' : 'Rating Distribution'}
                  </h3>
                  {stats.ratingsCount > 0 ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div style={{ fontSize: 36, fontWeight: 700, color: '#111827' }}>
                          {stats.averageRating.toFixed(1)}
                      </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill={star <= Math.round(stats.averageRating) ? '#f59e0b' : 'none'} stroke="#f59e0b" strokeWidth="1.5">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                              </svg>
                  ))}
                </div>
                          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                            {stats.ratingsCount} {isArabic ? 'تقييم' : 'reviews'}
                          </div>
                        </div>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <RatingBreakdown
                          teacherEmail={profile.email}
                          isArabic={isArabic}
                        />
                      </div>
                      <button
                        onClick={() => {
                          // Scroll to reviews section or navigate to reviews page
                          const reviewsSection = document.querySelector('[data-section="reviews"]');
                          if (reviewsSection) {
                            reviewsSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          borderRadius: 8,
                          border: '1px solid #e5e7eb',
                          background: '#fff',
                          color: '#111827',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.background = '#f9fafb';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = '#fff';
                        }}
                      >
                        {isArabic ? 'قراءة التقييمات' : 'Read Reviews'}
                      </button>
                    </>
                  ) : (
                  <div style={{
                      textAlign: 'center', 
                      padding: '40px 20px',
                      color: '#9ca3af',
                      fontSize: 14
                    }}>
                      {isArabic ? 'لا توجد تقييمات بعد' : 'No ratings yet'}
                  </div>
                )}
                </div>
              </div>

              {/* Recent Reviews Section */}
              <div
                data-section="reviews"
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 16 : 20,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  marginTop: isMobile ? 16 : 20
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 16
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
                    {isArabic ? 'التقييمات الأخيرة' : 'Recent Reviews'}
                  </h3>
                </div>
                <RecentReviews
                  teacherEmail={profile.email}
                  isArabic={isArabic}
                  limit={5}
                />
              </div>

              {/* Quick Actions - 3D Style Icons matching images */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? 12 : 16,
                marginTop: isMobile ? 0 : 8
              }}>
                {/* Calendar */}
                <button
                  onClick={() => navigate('/dashboard/teacher/calendar')}
                  style={{
            background: '#fff',
                    border: 'none',
                    borderRadius: 16,
                    padding: isMobile ? 20 : 24,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
                    gap: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                >
                  <div style={{
                    width: isMobile ? 64 : 80,
                    height: isMobile ? 64 : 80,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    position: 'relative',
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                      <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                    {isArabic ? 'التقويم' : 'Calendar'}
                  </span>
                </button>

                {/* Requests */}
                <button
                  onClick={() => navigate('/dashboard/teacher/requests')}
                  style={{
                    background: '#fff',
                    border: 'none',
                    borderRadius: 16,
                    padding: isMobile ? 20 : 24,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                >
                  <div style={{
                    width: isMobile ? 64 : 80,
                    height: isMobile ? 64 : 80,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #d97706 0%, #92400e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(217, 119, 6, 0.3)',
                    position: 'relative',
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10 9 9 9 8 9"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                    {isArabic ? 'الطلبات' : 'Requests'}
                  </span>
                </button>

                {/* Chat */}
                <button
                  onClick={() => navigate('/dashboard/teacher/chat')}
                  style={{
                    background: '#fff',
                    border: 'none',
                    borderRadius: 16,
                    padding: isMobile ? 20 : 24,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                >
                  <div style={{
                    width: isMobile ? 64 : 80,
                    height: isMobile ? 64 : 80,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(167, 139, 250, 0.3)',
                    position: 'relative',
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(5deg)'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      <line x1="9" y1="10" x2="15" y2="10" stroke="#fff" strokeWidth="2"/>
                      <line x1="9" y1="14" x2="13" y2="14" stroke="#fff" strokeWidth="2"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                    {isArabic ? 'المحادثة' : 'Chat'}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Profile Tab - Review Only */}
          {activeTab === 'profile' && (
            <div>
          {/* Review Section - Step10Review Component */}
          {wizardDataForReview && (
            <div style={{ width: '100%', padding: isMobile ? '16px 0' : '24px 0' }}>
              <Step10Review
                wizardData={wizardDataForReview}
                onBack={null}
                onSubmit={null}
                isSubmitting={false}
                translations={wizardTranslations[isArabic ? 'AR' : 'EN'] || {}}
                isArabic={isArabic}
                language={isArabic ? 'AR' : 'EN'}
                enableInlineEdit={true}
                onDataUpdate={handleProfileDataUpdate}
              />
              <div style={{
                marginTop: '24px',
                padding: '20px',
                background: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  {isArabic
                    ? 'انقر على أي قسم للتحرير مباشرة.'
                    : 'Click on any section to edit directly.'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              {/* Feedback/Support Form */}
              <FeedbackForm isArabic={isArabic} profile={profile} />
              
              {/* Password Change Section */}
              <div style={{ maxWidth: 600, margin: '0 auto' }}>
              {/* Change Password Section */}
              <div style={{
                background: '#f8fafc',
                borderRadius: 16,
                padding: 24,
                marginBottom: 24,
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 20 }}>
                  {t.changePassword}
                </div>
                <form onSubmit={handleChangePassword}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'block', color: '#374151' }}>
                      {t.oldPassword}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showOldPassword ? 'text' : 'password'}
                        name="oldPassword"
                        value={profile.oldPassword}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 45px 14px 14px',
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                          fontSize: 16,
                          background: '#fff',
                          outline: 'none',
                          transition: 'all 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: 18
                        }}
                      >
                        {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'block', color: '#374151' }}>
                      {t.newPassword}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={profile.newPassword}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 45px 14px 14px',
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                          fontSize: 16,
                          background: '#fff',
                          outline: 'none',
                          transition: 'all 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: 18
                        }}
                      >
                        {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'block', color: '#374151' }}>
                      {t.confirmPassword}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={profile.confirmPassword}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '14px 45px 14px 14px',
                          borderRadius: 12,
                          border: '2px solid #e5e7eb',
                          fontSize: 16,
                          background: '#fff',
                          outline: 'none',
                          transition: 'all 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: 18
                        }}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 16px rgba(255,116,38,0.3)',
                      transition: 'all 0.2s',
                      opacity: loading ? 0.7 : 1
                    }}
                    disabled={loading}
                  >
                    {loading ? (isArabic ? 'جارٍ التحديث...' : 'Updating...') : t.changePasswordBtn}
                  </button>
                </form>
              </div>

              {/* Logout Section */}
              <div style={{
                background: '#fef2f2',
                borderRadius: 16,
                padding: 24,
                border: '1px solid #fecaca'
              }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#991b1b', marginBottom: 12 }}>
                  {isArabic ? 'إدارة الحساب' : 'Account Management'}
                </div>
                <div style={{ fontSize: 14, color: '#7f1d1d', marginBottom: 20 }}>
                  {isArabic 
                    ? 'يمكنك تسجيل الخروج من حسابك في أي وقت'
                    : 'You can sign out of your account at any time'}
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    padding: '12px 24px',
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#b91c1c';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  {t.logout}
                </button>
              </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Image Crop Modal */}
      {showCropModal && imageToCrop && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)',
          padding: isMobile ? '16px' : '0'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? 16 : 20,
            padding: isMobile ? 16 : 24,
            maxWidth: isMobile ? '100%' : 600,
            width: isMobile ? '100%' : '90%',
            maxHeight: isMobile ? '95vh' : '90vh',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 16 : 20,
            overflowY: 'auto'
          }}>
            <div style={{ 
              fontSize: isMobile ? 20 : 24, 
              fontWeight: 700, 
              color: '#111827' 
            }}>
              {isArabic ? 'قص الصورة' : 'Crop Image'}
            </div>
            
            <div style={{
              position: 'relative',
              width: '100%',
              height: isMobile ? 280 : 400,
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              minHeight: isMobile ? 280 : 400
            }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: {
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                  },
                  cropAreaStyle: {
                    border: '2px solid #FF7426'
                  }
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 12 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 8 : 12,
                flexWrap: isMobile ? 'wrap' : 'nowrap'
              }}>
                <label style={{ 
                  fontSize: isMobile ? 13 : 14, 
                  fontWeight: 600, 
                  color: '#374151', 
                  minWidth: isMobile ? 50 : 60 
                }}>
                  {isArabic ? 'التكبير:' : 'Zoom:'}
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    height: isMobile ? 8 : 6,
                    borderRadius: 3,
                    background: '#e5e7eb',
                    outline: 'none',
                    cursor: 'pointer',
                    minWidth: isMobile ? 120 : 'auto'
                  }}
                />
                <span style={{ 
                  fontSize: isMobile ? 13 : 14, 
                  fontWeight: 600, 
                  color: '#6b7280', 
                  minWidth: isMobile ? 35 : 40 
                }}>
                  {Math.round(zoom * 100)}%
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? 10 : 12, 
                justifyContent: 'flex-end',
                flexDirection: isMobile ? 'column-reverse' : 'row',
                width: '100%'
              }}>
                <button
                  type="button"
                  onClick={handleCropCancel}
                  disabled={loading}
                  style={{
                    padding: isMobile ? '12px 20px' : '12px 24px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 15,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.5 : 1,
                    width: isMobile ? '100%' : 'auto',
                    touchAction: 'manipulation',
                    minHeight: isMobile ? '48px' : 'auto',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleCropComplete}
                  disabled={loading}
                  style={{
                    padding: isMobile ? '12px 20px' : '12px 24px',
                    background: 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 15,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.7 : 1,
                    boxShadow: '0 4px 16px rgba(255,116,38,0.3)',
                    width: isMobile ? '100%' : 'auto',
                    touchAction: 'manipulation',
                    minHeight: isMobile ? '48px' : 'auto',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {loading ? (isArabic ? 'جارٍ التحميل...' : 'Uploading...') : (isArabic ? 'حفظ' : 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
} 